import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  ConnectionMode,
  type Connection,
  type ReactFlowInstance
} from "reactflow";
import "reactflow/dist/style.css";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { hasCoordinateChanged } from "../../helpers/canvasPosition";
import { findFreePosition, NodeRect } from "../../helpers/nodePlacement";
import {
  getDistributionValidationError,
  isHashDistribution,
  isSqlPoolDedicated,
  normalizeDistributionType
} from "../../helpers/distribution";
import { SHARED_CANVAS_INTERACTION_PROPS } from "./shared/canvasInteraction";
import { useStatusToast } from "../../hooks/useStatusToast";
import { FiAlignJustify, FiDownload, FiGrid, FiPlay, FiPlus, 
  // FiShield
 } from "react-icons/fi";
import {
  createDataVaultMappings,
  deleteTargetEntityRdvLinkMember,
  deleteTargetEntityRdvNode,
  deleteTargetEntityRdvSatMember,
  generateRawDataVaultSqlPreview,
  generateDqTestRawDataVaultSqlPreview,
  type RawDataVaultSqlPreviewResponse,
  getDataVaultRelatedEntities,
  saveWorkspaceLayout,
  updateDataVaultMappings,
  type CreateMappingPayload,
  type WorkspaceLayoutEntry
} from "../../services/dataVaultModelling.service";

import { HubNode } from "./components/HubNode";
import { SatelliteNode } from "./components/SatelliteNode";
import { LinkNode } from "./components/LinkNode";
import { ManualNode } from "./components/ManualNode";
import { PromoteRdvAssetModal } from "./components/PromoteRdvAssetModal";
import { EntityDetailsPanel } from "./components/EntityDetailsPanel";
import {
  GenerationReportSummary,
  type GenerationReport
} from "../../components/modelling/GenerationReportSummary";
import { ValidationSummary } from "./components/ValidationSummary";
import { ToolbarButton } from "./components/ToolbarButton";
import { MappingModal } from "./components/MappingModal";
import { SqlPreview } from "./components/forms/SqlPreview";
import { buildLayoutFromRelatedEntities, buildModelFromRelatedEntities } from "./model/buildModel";
import { matchesCanvasFilter } from "../../helpers/canvasFilter";
import {
  checkNodeOverlaps,
  layoutClusters,
  type DataVaultNode
} from "./model/dataVaultSimulation";
import { formatLabel } from "./model/formatting";
import { computeValidation } from "./model/validation";
import { downloadFile } from "./model/files";
import {
  FlowNode,
  FlowEdge,
  type VaultRelationshipEdgeData,
  generateFlowElements,
  getEntityById,
  mergeNodes
} from "./workspace/flow";
import { useWorkspaceModel } from "./workspace/useWorkspaceModel";
import type {
  SelectedEntity,
  ValidationMessage,
  LayoutMap,
  HubEntity,
  LinkEntity,
  ManualEntity,
  SatelliteEntity,
  TableRecord,
  DataVaultModel
} from "./model/types";
import type { VaultNodeData, HubNodeData, SatelliteNodeData } from "./components/types";
import { mergeWorkspaceLayout, mergeWorkspaceSyncedLayout } from "../../redux/workspaceLayout/actions";
import type { IAppDispatch, IRootState } from "../../redux/store";
import { getScopeCustomerId, getScopeEnvList, getScopeEnvironmentId } from "../../services/activeScope";
import {
  SearchableSelect,
  type SearchableSelectOption
} from "../../components/SearchableSelect/SearchableSelect";

const isBrowser = typeof window !== "undefined";

const FALLBACK_ENVIRONMENT_ID = 114;
const FALLBACK_CUSTOMER_ID = 101;
const LAYOUT_NODE_WIDTH = 240;
const LAYOUT_NODE_HEIGHT = 90;
const LAYOUT_SPACING_X = 60;
const LAYOUT_SPACING_Y = 60;
const hasCanvasPosition = (node: DataVaultNode) =>
  typeof node.canvas_x === "number" && typeof node.canvas_y === "number";

const isOverlappingAny = (
  x: number,
  y: number,
  occupied: Array<{ x: number; y: number }>
) => occupied.some((item) => !(
  x + LAYOUT_NODE_WIDTH <= item.x ||
  item.x + LAYOUT_NODE_WIDTH <= x ||
  y + LAYOUT_NODE_HEIGHT <= item.y ||
  item.y + LAYOUT_NODE_HEIGHT <= y
));

const findNonOverlappingPosition = (
  preferredX: number,
  preferredY: number,
  occupied: Array<{ x: number; y: number }>
) => {
  if (!isOverlappingAny(preferredX, preferredY, occupied)) {
    return { x: preferredX, y: preferredY };
  }
  const stepX = LAYOUT_NODE_WIDTH + LAYOUT_SPACING_X;
  const stepY = LAYOUT_NODE_HEIGHT + LAYOUT_SPACING_Y;
  for (let ring = 1; ring < 200; ring += 1) {
    for (let dx = -ring; dx <= ring; dx += 1) {
      for (let dy = -ring; dy <= ring; dy += 1) {
        if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) {
          continue;
        }
        const candidateX = preferredX + dx * stepX;
        const candidateY = preferredY + dy * stepY;
        if (!isOverlappingAny(candidateX, candidateY, occupied)) {
          return { x: candidateX, y: candidateY };
        }
      }
    }
  }
  return { x: preferredX, y: preferredY };
};

const prepareLayoutEntities = (input: DataVaultNode[]): DataVaultNode[] => {
  if (!input.length) {
    return [];
  }

  const clusterMap = new Map<string, number>();
  let nextClusterId = 1;
  const clustered = input.map((node) => {
    const syntheticClusterId =
      typeof node.synthetic_cluster_id === "number" && Number.isFinite(node.synthetic_cluster_id)
        ? node.synthetic_cluster_id
        : null;
    if (syntheticClusterId != null) {
      return {
        ...node,
        synthetic_cluster_id: syntheticClusterId
      };
    }
    const key =
      node.subject_area?.trim().toLowerCase() ||
      node.ensemble?.trim().toLowerCase() ||
      "default";
    if (!clusterMap.has(key)) {
      clusterMap.set(key, nextClusterId);
      nextClusterId += 1;
    }
    return {
      ...node,
      synthetic_cluster_id: clusterMap.get(key)
    };
  });

  const pinned = clustered.filter(hasCanvasPosition);
  const toLayout = clustered.filter((node) => !hasCanvasPosition(node));
  if (!toLayout.length) {
    return clustered;
  }

  const positioned = layoutClusters(toLayout, {
    nodeWidth: LAYOUT_NODE_WIDTH,
    nodeHeight: LAYOUT_NODE_HEIGHT,
    spacingX: LAYOUT_SPACING_X,
    spacingY: LAYOUT_SPACING_Y
  });

  const occupied = pinned.map((node) => ({
    x: node.canvas_x as number,
    y: node.canvas_y as number
  }));
  const autoPositionByKey = new Map<string, { x: number; y: number }>();

  positioned.forEach((node) => {
    const x = node.canvas_x ?? 0;
    const y = node.canvas_y ?? 0;
    const slot = findNonOverlappingPosition(x, y, occupied);
    occupied.push(slot);
    const key = `${node.source_entity_id ?? "null"}|${node.target_entity_rdv_id ?? "null"}|${node.target_entity_rdv_name ?? ""}`;
    autoPositionByKey.set(key, slot);
  });

  const finalNodes = clustered.map((node) => {
    if (hasCanvasPosition(node)) {
      return node;
    }
    const key = `${node.source_entity_id ?? "null"}|${node.target_entity_rdv_id ?? "null"}|${node.target_entity_rdv_name ?? ""}`;
    const coords = autoPositionByKey.get(key);
    return {
      ...node,
      canvas_x: coords?.x ?? 0,
      canvas_y: coords?.y ?? 0
    };
  });

  const overlaps = checkNodeOverlaps(
    finalNodes,
    LAYOUT_NODE_WIDTH,
    LAYOUT_NODE_HEIGHT
  );
  if (overlaps.length > 0) {
    console.warn(`Layout overlap check failed with ${overlaps.length} conflicts.`);
  }

  return finalNodes;
};


const parseNumericId = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getWorkspaceContextIds = () => {
  if (typeof window === "undefined") {
    return {
      environmentId: FALLBACK_ENVIRONMENT_ID,
      customerId: FALLBACK_CUSTOMER_ID
    };
  }
  const environmentRaw = getScopeEnvironmentId();
  const customerRaw = getScopeCustomerId();
  return {
    environmentId: parseNumericId(environmentRaw) ?? FALLBACK_ENVIRONMENT_ID,
    customerId: parseNumericId(customerRaw) ?? FALLBACK_CUSTOMER_ID
  };
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (error && typeof error === "object") {
    const maybeError = error as {
      response?: { data?: { detail?: unknown } };
      resBody?: { detail?: unknown };
      body?: { detail?: unknown };
    };
    const detail =
      maybeError.response?.data?.detail ??
      maybeError.resBody?.detail ??
      maybeError.body?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail
        .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
        .join(", ");
    }
  }
  return fallback;
};

const getActiveEnvironmentDwTargetType = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const envListRaw = getScopeEnvList();
    const environmentIdRaw = getScopeEnvironmentId();
    if (!envListRaw || !environmentIdRaw) {
      return null;
    }
    const parsed = JSON.parse(envListRaw);
    const environmentId = Number(environmentIdRaw);
    if (!Array.isArray(parsed) || Number.isNaN(environmentId)) {
      return null;
    }
    const activeEnvironment = parsed.find((env) => {
      if (!env) {
        return false;
      }
      const candidateId =
        typeof env.environment_id === "number"
          ? env.environment_id
          : Number(env.environment_id);
      return Number.isFinite(candidateId) && candidateId === environmentId;
    });
    // On-prem target takes precedence when set (Aleris uses dw_target_types_onprem =
    // LAKEHOUSE); mirrors the backend resolver in app/api/automation.py.
    const onpremTargetType = activeEnvironment?.dw_target_types_onprem;
    if (typeof onpremTargetType === "string" && onpremTargetType.trim()) {
      return onpremTargetType.trim();
    }
    const targetType = activeEnvironment?.dw_target_types;
    if (typeof targetType === "string" && targetType.trim()) {
      return targetType.trim();
    }
  } catch (error) {
    console.warn("Unable to resolve environment dw_target_types", error);
  }
  return null;
};

const isLakehouseTargetType = (targetType: string | null): boolean =>
  (targetType ?? "").trim().toUpperCase() === "LAKEHOUSE";

const buildLayoutSyncPayload = (
  model: DataVaultModel | null,
  layout: LayoutMap,
  previousLayout: LayoutMap
): WorkspaceLayoutEntry[] => {
  if (!model) {
    return [];
  }
  const entries: WorkspaceLayoutEntry[] = [];
  const pushEntry = (
    entityType: WorkspaceLayoutEntry["entity_type"],
    entity: HubEntity | SatelliteEntity | LinkEntity | ManualEntity
  ) => {
    const coords = layout[entity.id];
    const targetTable = entity.record.target_table?.trim();
    if (!coords || !targetTable) {
      return;
    }
    const previousCoords = previousLayout[entity.id];
    if (!hasCoordinateChanged(coords, previousCoords)) {
      return;
    }
    entries.push({
      entity_type: entityType,
      target_table: targetTable,
      target_entity_rdv_id: parseNumericId(entity.record.target_entity_rdv_id),
      canvas_x: coords.x,
      canvas_y: coords.y
    });
  };
  model.hubs.forEach((hub) => pushEntry("hub", hub));
  model.satellites.forEach((satellite) => pushEntry("satellite", satellite));
  model.links.forEach((link) => pushEntry("link", link));
  model.manual.forEach((manualEntity) => pushEntry("manual", manualEntity));
  return entries;
};

const buildSyncedLayoutDelta = (
  model: DataVaultModel | null,
  entries: WorkspaceLayoutEntry[],
  layout: LayoutMap
): LayoutMap => {
  if (!model || !entries.length) {
    return {};
  }
  const delta: LayoutMap = {};
  const applyEntry = (
    entity: HubEntity | SatelliteEntity | LinkEntity | ManualEntity,
    entry: WorkspaceLayoutEntry
  ) => {
    const coords = layout[entity.id];
    if (coords) {
      delta[entity.id] = coords;
    }
  };

  entries.forEach((entry) => {
    if (entry.entity_type === "hub") {
      const entity = model.hubs.find(
        (hub) => parseNumericId(hub.record.target_entity_rdv_id) === entry.target_entity_rdv_id
      );
      if (entity) {
        applyEntry(entity, entry);
      }
      return;
    }
    if (entry.entity_type === "satellite") {
      const entity = model.satellites.find(
        (satellite) =>
          parseNumericId(satellite.record.target_entity_rdv_id) === entry.target_entity_rdv_id
      );
      if (entity) {
        applyEntry(entity, entry);
      }
      return;
    }
    if (entry.entity_type === "manual") {
      const entity = model.manual.find(
        (manualEntity) =>
          parseNumericId(manualEntity.record.target_entity_rdv_id) === entry.target_entity_rdv_id
      );
      if (entity) {
        applyEntry(entity, entry);
      }
      return;
    }
    const entity = model.links.find(
      (link) => parseNumericId(link.record.target_entity_rdv_id) === entry.target_entity_rdv_id
    );
    if (entity) {
      applyEntry(entity, entry);
    }
  });

  return delta;
};

const buildAutoArrangeLayout = (model: DataVaultModel): LayoutMap => {
  const parentById = new Map<string, string>();
  const recordById = new Map<string, TableRecord>();
  const addEntity = (id: string, record: TableRecord) => {
    parentById.set(id, id);
    recordById.set(id, record);
  };
  const findRoot = (id: string): string => {
    const parent = parentById.get(id);
    if (!parent || parent === id) {
      return id;
    }
    const root = findRoot(parent);
    parentById.set(id, root);
    return root;
  };
  const union = (left: string | null | undefined, right: string | null | undefined) => {
    if (!left || !right || !parentById.has(left) || !parentById.has(right)) {
      return;
    }
    const leftRoot = findRoot(left);
    const rightRoot = findRoot(right);
    if (leftRoot !== rightRoot) {
      parentById.set(rightRoot, leftRoot);
    }
  };

  model.hubs.forEach((hub) => addEntity(hub.id, hub.record));
  model.satellites.forEach((satellite) => addEntity(satellite.id, satellite.record));
  model.links.forEach((link) => addEntity(link.id, link.record));
  model.satellites.forEach((satellite) => union(satellite.id, satellite.parentHubId));
  model.links.forEach((link) => {
    link.connectedHubIds.forEach((hubId) => union(link.id, hubId));
    link.satelliteIds.forEach((satelliteId) => union(link.id, satelliteId));
  });

  const componentMembers = new Map<string, string[]>();
  parentById.forEach((_, id) => {
    const root = findRoot(id);
    componentMembers.set(root, [...(componentMembers.get(root) ?? []), id]);
  });
  const orderedComponents = Array.from(componentMembers.entries()).sort(([, leftIds], [, rightIds]) => {
    const labelFor = (ids: string[]) =>
      ids
        .map((id) => {
          const record = recordById.get(id);
          return (
            record?.subject_area?.trim().toLowerCase() ||
            record?.ensemble?.trim().toLowerCase() ||
            record?.target_table?.trim().toLowerCase() ||
            id
          );
        })
        .sort()[0] ?? "";
    return labelFor(leftIds).localeCompare(labelFor(rightIds));
  });
  const clusterIdByEntityId = new Map<string, number>();
  orderedComponents.forEach(([, ids], index) => {
    ids.forEach((id) => clusterIdByEntityId.set(id, index + 1));
  });
  const hubById = new Map(model.hubs.map((hub) => [hub.id, hub]));
  const nodes: Array<DataVaultNode & { workspace_id: string }> = [
    ...model.hubs.map((hub) => ({
      source_entity_id: parseNumericId(hub.record.source_entity_id),
      source_entity_name: hub.record.source_entity_name ?? null,
      data_source_name: hub.record.data_source_name ?? null,
      data_source_type: hub.record.data_source_type ?? null,
      target_entity_rdv_id: parseNumericId(hub.record.target_entity_rdv_id),
      target_entity_rdv_name: hub.record.target_entity_rdv_name ?? hub.record.target_table ?? hub.id,
      target_schema: hub.record.target_schema ?? null,
      ensemble: hub.record.ensemble ?? null,
      subject_area: hub.record.subject_area ?? null,
      source_filter: hub.record.source_filter ?? null,
      table_construct: hub.record.datavault_construct_type ?? null,
      synthetic_cluster_id: clusterIdByEntityId.get(hub.id) ?? 1,
      workspace_id: hub.id,
      sat_hubs: model.satellites
        .filter((satellite) => satellite.parentHubId === hub.id)
        .map((satellite) => ({
          sat_target_entity_rdv_id: parseNumericId(satellite.record.target_entity_rdv_id),
          sat_target_entity_rdv_name:
            satellite.record.target_entity_rdv_name ?? satellite.record.target_table ?? satellite.id
        }))
    })),
    ...model.satellites.map((satellite) => ({
      source_entity_id: parseNumericId(satellite.record.source_entity_id),
      source_entity_name: satellite.record.source_entity_name ?? null,
      data_source_name: satellite.record.data_source_name ?? null,
      data_source_type: satellite.record.data_source_type ?? null,
      target_entity_rdv_id: parseNumericId(satellite.record.target_entity_rdv_id),
      target_entity_rdv_name:
        satellite.record.target_entity_rdv_name ?? satellite.record.target_table ?? satellite.id,
      target_schema: satellite.record.target_schema ?? null,
      ensemble: satellite.record.ensemble ?? null,
      subject_area: satellite.record.subject_area ?? null,
      source_filter: satellite.record.source_filter ?? null,
      table_construct: satellite.record.datavault_construct_type ?? null,
      synthetic_cluster_id: clusterIdByEntityId.get(satellite.id) ?? 1,
      workspace_id: satellite.id
    })),
    ...model.links.map((link) => ({
      source_entity_id: parseNumericId(link.record.source_entity_id),
      source_entity_name: link.record.source_entity_name ?? null,
      data_source_name: link.record.data_source_name ?? null,
      data_source_type: link.record.data_source_type ?? null,
      target_entity_rdv_id: parseNumericId(link.record.target_entity_rdv_id),
      target_entity_rdv_name: link.record.target_entity_rdv_name ?? link.record.target_table ?? link.id,
      target_schema: link.record.target_schema ?? null,
      ensemble: link.record.ensemble ?? null,
      subject_area: link.record.subject_area ?? null,
      source_filter: link.record.source_filter ?? null,
      table_construct: link.record.datavault_construct_type ?? null,
      synthetic_cluster_id: clusterIdByEntityId.get(link.id) ?? 1,
      workspace_id: link.id,
      linked_hubs: link.connectedHubIds
        .map((hubId) => hubById.get(hubId))
        .filter((hub): hub is HubEntity => Boolean(hub))
        .map((hub) => ({
          hub_target_entity_rdv_id: parseNumericId(hub.record.target_entity_rdv_id),
          hub_target_entity_rdv_name: hub.record.target_entity_rdv_name ?? hub.record.target_table ?? hub.id
        }))
    }))
  ];

  const arranged = layoutClusters(nodes, {
    nodeWidth: LAYOUT_NODE_WIDTH,
    nodeHeight: LAYOUT_NODE_HEIGHT,
    spacingX: 95,
    spacingY: 82,
    clusterGapX: 240,
    clusterGapY: 210,
    clustersPerRow: 3,
    originX: 0,
    originY: 0
  }) as Array<DataVaultNode & { workspace_id: string }>;

  return arranged.reduce<LayoutMap>((layout, node) => {
    if (typeof node.canvas_x === "number" && typeof node.canvas_y === "number") {
      layout[node.workspace_id] = { x: node.canvas_x, y: node.canvas_y };
    }
    return layout;
  }, {});
};

const nodeTypes = {
  hubNode: HubNode,
  satelliteNode: SatelliteNode,
  linkNode: LinkNode,
  manualNode: ManualNode
} as const;

const PLACEMENT_NODE_WIDTH = 340;
const PLACEMENT_NODE_HEIGHT = 190;
const FIT_VIEW_PADDING = 0.6;
const CREATED_NODE_FOCUS_ZOOM = 1.2;
const CREATED_NODE_FOCUS_RETRIES = 12;
type EntityKind = "hub" | "satellite" | "link" | "manual";
type VaultFilterField = "" | "subject_area" | "ensemble" | "target_entity_rdv_name";
type ActiveVaultFilter = {
  field: Exclude<VaultFilterField, "">;
  value: string;
};

type SnapshotState = {
  hubs: Map<string, HubEntity>;
  satellites: Map<string, SatelliteEntity>;
  links: Map<string, LinkEntity>;
  manual: Map<string, ManualEntity>;
};

const buildEntityKey = (type: EntityKind, id: string) => `${type}:${id}`;

const cloneRecord = (record: TableRecord): TableRecord => ({ ...record });

const snapshotHub = (hub: HubEntity): HubEntity => ({
  ...hub,
  record: cloneRecord(hub.record)
});

const snapshotSatellite = (satellite: SatelliteEntity): SatelliteEntity => ({
  ...satellite,
  record: cloneRecord(satellite.record)
});

const snapshotLink = (link: LinkEntity): LinkEntity => ({
  ...link,
  record: cloneRecord(link.record),
  linkedHubs: link.linkedHubs ? link.linkedHubs.map((hub) => ({ ...hub })) : undefined
});

const snapshotManual = (manual: ManualEntity): ManualEntity => ({
  ...manual,
  record: cloneRecord(manual.record)
});

const normalizeComparableString = (value: string | null | undefined): string => (value ?? "").trim();

const normalizeIdListForComparison = (values: string[]) => [...values].sort((a, b) => a.localeCompare(b));

const normalizeLinkedHubsForComparison = (linkedHubs: LinkEntity["linkedHubs"]) =>
  (linkedHubs ?? [])
    .map((hub) => ({
      hub_target_entity_rdv_id: normalizeComparableString(hub.hub_target_entity_rdv_id),
      hub_target_entity_rdv_name: normalizeComparableString(hub.hub_target_entity_rdv_name),
      hub_role: normalizeComparableString(hub.hub_role)
    }))
    .sort((a, b) =>
      `${a.hub_target_entity_rdv_id}:${a.hub_target_entity_rdv_name}:${a.hub_role}`.localeCompare(
        `${b.hub_target_entity_rdv_id}:${b.hub_target_entity_rdv_name}:${b.hub_role}`
      )
    );

const HUB_DETAIL_FIELDS: (keyof TableRecord)[] = [
  "target_table",
  "target_schema",
  "subject_area",
  "ensemble",
  "data_source_name",
  "data_source_id",
  "source_entity_name",
  "source_entity_id",
  "source_filter",
  "dq_source_filter",
  "Description",
  "datavault_construct_type",
  "include_dq_test",
  "generate_model_entity",
  "is_custom_view_or_table",
  "distribution_type",
  "distribution_key"
];

const SATELLITE_DETAIL_FIELDS: (keyof TableRecord)[] = [
  "target_table",
  "target_schema",
  "subject_area",
  "ensemble",
  "data_source_name",
  "data_source_id",
  "source_entity_name",
  "source_entity_id",
  "source_filter",
  "dq_source_filter",
  "Description",
  "datavault_construct_type",
  "include_dq_test",
  "generate_model_entity",
  "is_custom_view_or_table",
  "distribution_type",
  "distribution_key"
];

// Editable fields on a Manual entity (the only ones the panel exposes — subject area + description).
const MANUAL_DETAIL_FIELDS: (keyof TableRecord)[] = ["subject_area", "Description"];

const LINK_DETAIL_FIELDS: (keyof TableRecord)[] = [
  "target_table",
  "target_schema",
  "subject_area",
  "ensemble",
  "data_source_name",
  "data_source_id",
  "source_entity_name",
  "source_entity_id",
  "source_filter",
  "dq_source_filter",
  "Description",
  "datavault_construct_type",
  "include_dq_test",
  "generate_model_entity",
  "is_custom_view_or_table",
  "exclude_null_business_keys",
  "distribution_type",
  "distribution_key"
];

const recordsMatchOnFields = (
  current: TableRecord,
  snapshot: TableRecord,
  fields: (keyof TableRecord)[]
): boolean =>
  fields.every((field) => {
    const currentValue = normalizeComparableString(
      current[field] as string | null | undefined
    );
    const snapshotValue = normalizeComparableString(
      snapshot[field] as string | null | undefined
    );
    return currentValue === snapshotValue;
  });

const isSelectedEntityDirtyAgainstSnapshot = (
  selectedEntity: Exclude<SelectedEntity, null> | null,
  snapshots: SnapshotState
): boolean => {
  if (!selectedEntity) {
    return false;
  }

  if (selectedEntity.type === "hub") {
    const snapshot = snapshots.hubs.get(selectedEntity.entity.id);
    if (!snapshot) {
      return true;
    }
    return !recordsMatchOnFields(
      selectedEntity.entity.record,
      snapshot.record,
      HUB_DETAIL_FIELDS
    );
  }

  if (selectedEntity.type === "satellite") {
    const snapshot = snapshots.satellites.get(selectedEntity.entity.id);
    if (!snapshot) {
      return true;
    }
    return (
      !recordsMatchOnFields(
      selectedEntity.entity.record,
      snapshot.record,
      SATELLITE_DETAIL_FIELDS
      ) || selectedEntity.entity.parentHubId !== snapshot.parentHubId
    );
  }

  if (selectedEntity.type === "manual") {
    const snapshot = snapshots.manual.get(selectedEntity.entity.id);
    if (!snapshot) {
      return true;
    }
    return !recordsMatchOnFields(
      selectedEntity.entity.record,
      snapshot.record,
      MANUAL_DETAIL_FIELDS
    );
  }

  const snapshot = snapshots.links.get(selectedEntity.entity.id);
  if (!snapshot) {
    return true;
  }

  const currentConnected = JSON.stringify(
    normalizeIdListForComparison(selectedEntity.entity.connectedHubIds)
  );
  const snapshotConnected = JSON.stringify(
    normalizeIdListForComparison(snapshot.connectedHubIds)
  );
  const currentSatellites = JSON.stringify(
    normalizeIdListForComparison(selectedEntity.entity.satelliteIds)
  );
  const snapshotSatellites = JSON.stringify(
    normalizeIdListForComparison(snapshot.satelliteIds)
  );
  const currentLinked = JSON.stringify(
    normalizeLinkedHubsForComparison(selectedEntity.entity.linkedHubs)
  );
  const snapshotLinked = JSON.stringify(
    normalizeLinkedHubsForComparison(snapshot.linkedHubs)
  );
  return (
    !recordsMatchOnFields(
      selectedEntity.entity.record,
      snapshot.record,
      LINK_DETAIL_FIELDS
    ) ||
    currentConnected !== snapshotConnected ||
    currentSatellites !== snapshotSatellites ||
    currentLinked !== snapshotLinked
  );
};

const stripHubSuffix = (value: string): string => value.replace(/_h$/i, "") || value;

const parseBooleanFlag = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.toString().trim().toLowerCase();
  return normalized === "y" || normalized === "yes" || normalized === "true";
};

const buildLinkTargetTable = (hubIds: string[], hubs: HubEntity[]): string | null => {
  if (!hubIds.length) {
    return null;
  }
  const hubNames = hubIds
    .map((hubId) => {
      const hubRecord = hubs.find((hub) => hub.id === hubId)?.record;
      const rawName = hubRecord?.target_table?.trim() || hubId;
      return stripHubSuffix(rawName);
    })
    .filter((name): name is string => Boolean(name?.trim()));
  if (!hubNames.length) {
    return null;
  }
  return `${hubNames.join("__")}_l`;
};

/**
 * Distribution only exists on dedicated SQL pool targets. Prefer the workspace-level
 * value (on-prem aware) and fall back to the environment values echoed on the record.
 */
const isDistributionEditable = (
  record: TableRecord,
  workspaceTargetType: string | null | undefined
): boolean =>
  isSqlPoolDedicated(
    workspaceTargetType ||
      (record.dw_target_types_onprem ?? "").trim() ||
      record.dw_target_types
  );

type PayloadContext = {
  parentHub?: HubEntity | null;
  allHubs?: HubEntity[];
  position?: { x: number; y: number } | null;
  /** True only for dedicated SQL pool environments, where DISTRIBUTION is editable. */
  includeDistribution?: boolean;
};

const buildPayloadFromEntity = (
  entity: HubEntity | SatelliteEntity | LinkEntity | ManualEntity,
  type: EntityKind,
  context?: PayloadContext
): CreateMappingPayload => {
  const record = entity.record;
  const constructType = record.datavault_construct_type;
  const skipMetadata =
    typeof record.exclude_metadata_tests === "string"
      ? record.exclude_metadata_tests.trim().toLowerCase() === "y" ||
        record.exclude_metadata_tests.trim().toLowerCase() === "true"
      : Boolean(record.exclude_metadata_tests);
  const includeDqTest = parseBooleanFlag(record.include_dq_test);
  const generateModelEntity = parseBooleanFlag(record.generate_model_entity);
  const isCustomViewOrTable = parseBooleanFlag(record.is_custom_view_or_table);
  const excludeNullBusinessKeys = parseBooleanFlag(record.exclude_null_business_keys);
  const payload: CreateMappingPayload = {
    data_source_name: record.data_source_name ?? "",
    source_table: {
      value: record.source_entity_name ?? record.target_table ?? "",
      id: record.source_entity_id ? Number(record.source_entity_id) : undefined
    },
    subject_area: record.subject_area ?? "",
    ensemble: record.ensemble ?? "",
    data_vault_construct_type: constructType,
    skip_metadata_tests: skipMetadata,
    include_dq_test: includeDqTest,
    generate_model_entity: generateModelEntity,
    is_custom_view_or_table: isCustomViewOrTable,
    exclude_null_business_keys: excludeNullBusinessKeys,
    target_schema: record.target_schema || "rdv",
    target_table: {
      value: record.target_table || "",
      id: record.target_entity_rdv_id ? Number(record.target_entity_rdv_id) : undefined
    },
    source_filter: record.source_filter ?? "",
    dq_source_filter: record.dq_source_filter ?? ""
  };
  // Distribution was never sent, so every save dropped the modelled value and new
  // entities landed in the database with NULL — invalid SQL on a dedicated SQL pool.
  // Only sent where the blade actually edits it; the backend leaves the stored value
  // alone when the keys are absent, so other targets keep their migrated values.
  if (context?.includeDistribution) {
    const distributionType = normalizeDistributionType(record.distribution_type);
    payload.distribution_type = distributionType || null;
    payload.distribution_key = isHashDistribution(distributionType)
      ? (record.distribution_key ?? "").trim() || null
      : null;
  }
  if (type === "link") {
    const link = entity as LinkEntity;
    const hubRecordsById = new Map((context?.allHubs ?? []).map((hub) => [hub.id, hub]));
    const linkedHubsPayload = link.connectedHubIds
      .map((hubId) => {
        const hubRecord = hubRecordsById.get(hubId);
        const existingMetadata = (link.linkedHubs ?? []).find(
          (hub) =>
            String(hub.hub_target_entity_rdv_id ?? "") ===
              String(hubRecord?.record.target_entity_rdv_id ?? "") ||
            hub.hub_target_entity_rdv_name === hubRecord?.record.target_table ||
            hub.hub_target_entity_rdv_name === hubId
        );
        const hubIdNumber = hubRecord?.record.target_entity_rdv_id
          ? Number(hubRecord.record.target_entity_rdv_id)
          : existingMetadata?.hub_target_entity_rdv_id
            ? Number(existingMetadata.hub_target_entity_rdv_id)
            : undefined;
        const hubName =
          hubRecord?.record.target_table ||
          existingMetadata?.hub_target_entity_rdv_name ||
          undefined;
        if (!hubIdNumber && !hubName) {
          return null;
        }
        return {
          hub_target_entity_rdv_id: hubIdNumber,
          hub_target_entity_rdv_name: hubName,
          hub_role: existingMetadata?.hub_role ?? undefined
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    if (linkedHubsPayload.length) {
      payload.linked_hubs = linkedHubsPayload;
    }
  }
  if (type === "satellite") {
    const satellite = entity as SatelliteEntity;
    const parentHub = satellite.parentHubId ? context?.parentHub : null;
    if (satellite.parentHubId && parentHub) {
      const hubIdNumber = parentHub.record.target_entity_rdv_id
        ? Number(parentHub.record.target_entity_rdv_id)
        : undefined;
      const hubName = parentHub.record.target_table || satellite.parentHubId || undefined;
      if (hubIdNumber != null || hubName) {
        payload.parent_hub = {
          hub_target_entity_rdv_id: hubIdNumber,
          hub_target_entity_rdv_name: hubName
        };
      }
    }
  }
  const resolvedPosition = context?.position;
  if (resolvedPosition) {
    payload.canvas_x = resolvedPosition.x;
    payload.canvas_y = resolvedPosition.y;
  }
  return payload;
};

function MappingWorkspaceInner(): JSX.Element {
  const dispatch = useDispatch<IAppDispatch>();
  const persistedLayout = useSelector((state: IRootState) => state.workspaceLayout.layout);
  const persistedSyncedLayout = useSelector(
    (state: IRootState) => state.workspaceLayout.syncedLayout
  );
  const savedLayoutRef = useRef<LayoutMap>(persistedLayout);
  const syncedLayoutRef = useRef<LayoutMap>(persistedSyncedLayout);
  const isMountedRef = useRef(true);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);
  const pendingCreatedNodeFocusRef = useRef<string | null>(null);
  const searchFocusKeyRef = useRef("");
  const playSqlCopyFeedbackTimeoutRef = useRef<number | undefined>(undefined);
  const { statusMessage, showStatus } = useStatusToast();
  const activeDwTargetType = useMemo(() => getActiveEnvironmentDwTargetType(), []);
  const isLakehouseTarget = useMemo(
    () => isLakehouseTargetType(activeDwTargetType),
    [activeDwTargetType]
  );

  const [nodes, setNodes] = useNodesState<VaultNodeData>([]);
  const [edges, setEdges] = useEdgesState<VaultRelationshipEdgeData>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [selectedRelationshipEdgeId, setSelectedRelationshipEdgeId] = useState<string | null>(null);
  const [deletingRelationshipEdgeId, setDeletingRelationshipEdgeId] = useState<string | null>(null);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [, setDirtyEntities] = useState<Set<string>>(new Set());
  const snapshotsRef = useRef<SnapshotState>({
    hubs: new Map(),
    satellites: new Map(),
    links: new Map(),
    manual: new Map()
  });
  const [savingEntityId, setSavingEntityId] = useState<string | null>(null);
  const [subjectAreaOptions, setSubjectAreaOptions] = useState<string[]>([]);
  const [nodeSearchQuery, setNodeSearchQuery] = useState("");
  const [isFilterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterField, setFilterField] = useState<VaultFilterField>("");
  const [filterValueDraft, setFilterValueDraft] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveVaultFilter | null>(null);
  // Hubs whose satellites are collapsed (hidden) on the canvas — keeps large enterprise models
  // readable. Toggled from the hub node's chevron.
  const [collapsedHubIds, setCollapsedHubIds] = useState<Set<string>>(new Set());
  const handleToggleHubCollapse = useCallback((hubId: string) => {
    setCollapsedHubIds((previous) => {
      const next = new Set(previous);
      if (next.has(hubId)) {
        next.delete(hubId);
      } else {
        next.add(hubId);
      }
      return next;
    });
  }, []);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [deleteOverlay, setDeleteOverlay] = useState<{
    type: EntityKind;
    name: string;
  } | null>(null);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  // Run modal preview mode: model SQL (default) vs DQ test SQL for the same subject area.
  const [playMode, setPlayMode] = useState<"model" | "dq">("model");
  const [playSubjectArea, setPlaySubjectArea] = useState<string>("");
  const [playGenerateInserts, setPlayGenerateInserts] = useState(false);
  const [playFileSystem, setPlayFileSystem] = useState("");
  const [playSqlPreview, setPlaySqlPreview] = useState("");
  const [isPlaySqlLoading, setIsPlaySqlLoading] = useState(false);
  const [playSqlError, setPlaySqlError] = useState<string | null>(null);
  const [playSqlWarning, setPlaySqlWarning] = useState<string | null>(null);
  // Coverage of the last model-SQL run: how many of the subject area's tables actually
  // came back, and why the rest did not.
  const [playGenerationReport, setPlayGenerationReport] = useState<GenerationReport | null>(
    null
  );
  const [isPlaySqlStale, setIsPlaySqlStale] = useState(false);
  const [validationVisible, setValidationVisible] = useState(false);
  const [playSqlCopyStatus, setPlaySqlCopyStatus] = useState<"idle" | "success" | "error">("idle");

  const normalizeSubjectAreaValue = useCallback((value: string): string => value.trim(), []);

  useEffect(() => {
    savedLayoutRef.current = persistedLayout;
  }, [persistedLayout]);

  useEffect(() => {
    syncedLayoutRef.current = persistedSyncedLayout;
  }, [persistedSyncedLayout]);

  const getNodePosition = useCallback(
    (entityId: string) => {
      const node = nodes.find((candidate) => candidate.id === entityId);
      if (node?.position) {
        return node.position;
      }
      return savedLayoutRef.current[entityId] ?? null;
    },
    [nodes]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (playSqlCopyFeedbackTimeoutRef.current) {
        window.clearTimeout(playSqlCopyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const {
    model,
    setModel,
    addHub,
    addSatellite,
    addLink,
    removeHub,
    removeSatellite,
    removeLink,
    removeManual,
    updateHubField,
    updateManualField,
    updateSatelliteField,
    updateLinkField,
    updateSatelliteParent,
    updateLinkHubs
  } = useWorkspaceModel(showStatus, { defaultDwTargetType: activeDwTargetType });

  const hasUnsavedLayoutChanges = useMemo(() => {
    if (!model) {
      return false;
    }
    const persistedEntityIds = new Set<string>();
    model.hubs.forEach((hub) => {
      if (parseNumericId(hub.record.target_entity_rdv_id)) {
        persistedEntityIds.add(hub.id);
      }
    });
    model.satellites.forEach((satellite) => {
      if (parseNumericId(satellite.record.target_entity_rdv_id)) {
        persistedEntityIds.add(satellite.id);
      }
    });
    model.links.forEach((link) => {
      if (parseNumericId(link.record.target_entity_rdv_id)) {
        persistedEntityIds.add(link.id);
      }
    });
    return nodes.some(
      (node) =>
        persistedEntityIds.has(node.id) &&
        hasCoordinateChanged(node.position, persistedSyncedLayout[node.id])
    );
  }, [model, nodes, persistedSyncedLayout]);

  // Warn before leaving with unsaved canvas arrangement (layout persists only on Save Layout).
  useUnsavedChangesGuard(hasUnsavedLayoutChanges);

  const markEntityDirty = useCallback((type: EntityKind, id: string) => {
    setDirtyEntities((previous) => {
      const key = buildEntityKey(type, id);
      if (previous.has(key)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(key);
      return next;
    });
  }, []);

  const markEntitySaved = useCallback((type: EntityKind, id: string) => {
    setDirtyEntities((previous) => {
      const key = buildEntityKey(type, id);
      if (!previous.has(key)) {
        return previous;
      }
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
  }, []);

  const reserveCreatedNodePosition = useCallback(
    (createdId: string) => {
      const instance = reactFlowInstanceRef.current;
      const viewportCenter = instance?.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      }) ?? { x: 0, y: 0 };
      const rendered = instance?.getNodes() ?? [];
      const renderedIds = new Set(rendered.map((node) => node.id));
      const occupied: NodeRect[] = rendered.map((node) => {
        const origin = node.positionAbsolute ?? node.position;
        return {
          x: origin.x,
          y: origin.y,
          width: node.width ?? PLACEMENT_NODE_WIDTH,
          height: node.height ?? PLACEMENT_NODE_HEIGHT
        };
      });
      Object.entries(savedLayoutRef.current).forEach(([id, saved]) => {
        if (id !== createdId && !renderedIds.has(id)) {
          occupied.push({
            x: saved.x,
            y: saved.y,
            width: PLACEMENT_NODE_WIDTH,
            height: PLACEMENT_NODE_HEIGHT
          });
        }
      });
      const position = findFreePosition(
        {
          x: viewportCenter.x - PLACEMENT_NODE_WIDTH / 2,
          y: viewportCenter.y - PLACEMENT_NODE_HEIGHT / 2
        },
        { width: PLACEMENT_NODE_WIDTH, height: PLACEMENT_NODE_HEIGHT },
        occupied
      );
      savedLayoutRef.current = {
        ...savedLayoutRef.current,
        [createdId]: position
      };
      dispatch(mergeWorkspaceLayout({ [createdId]: position }));
    },
    [dispatch]
  );

  const applyEntityRecordUpdates = useCallback(
    (type: EntityKind, id: string, updates: Partial<TableRecord>) => {
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        if (type === "hub") {
          const previousHubRecord = previous.hubs.find((hub) => hub.id === id)?.record;
          const updatedHubs = previous.hubs.map((hub) =>
            hub.id === id
              ? {
                  ...hub,
                  record: {
                    ...hub.record,
                    ...updates
                  }
                }
              : hub
          );
          const updatedHubRecord = updatedHubs.find((hub) => hub.id === id)?.record;
          const updatedLinks = previous.links.map((link) => {
            if (!link.linkedHubs?.length || !updatedHubRecord) {
              return link;
            }
            const nextLinked = link.linkedHubs.map((entry) => {
              const matchesPrevious =
                entry.hub_target_entity_rdv_name ===
                (previousHubRecord?.target_table || id);
              if (!matchesPrevious) {
                return entry;
              }
              return {
                ...entry,
                hub_target_entity_rdv_id:
                  updates.target_entity_rdv_id ?? entry.hub_target_entity_rdv_id,
                hub_target_entity_rdv_name:
                  updatedHubRecord.target_table || entry.hub_target_entity_rdv_name
              };
            });
            return {
              ...link,
              linkedHubs: nextLinked
            };
          });
          return {
            ...previous,
            hubs: updatedHubs,
            links: updatedLinks
          };
        }
        if (type === "satellite") {
          return {
            ...previous,
            satellites: previous.satellites.map((satellite) =>
              satellite.id === id
                ? {
                    ...satellite,
                    record: {
                      ...satellite.record,
                      ...updates
                    }
                  }
                : satellite
            )
          };
        }
        return {
          ...previous,
          links: previous.links.map((link) =>
            link.id === id
              ? {
                  ...link,
                  record: {
                    ...link.record,
                    ...updates
                  }
                }
              : link
          )
        };
      });
    },
    [setModel]
  );

  const updateSnapshotForEntity = useCallback(
    (type: EntityKind, entity: HubEntity | SatelliteEntity | LinkEntity | ManualEntity) => {
      if (type === "hub") {
        snapshotsRef.current.hubs.set(entity.id, snapshotHub(entity as HubEntity));
        return;
      }
      if (type === "satellite") {
        snapshotsRef.current.satellites.set(
          entity.id,
          snapshotSatellite(entity as SatelliteEntity)
        );
        return;
      }
      if (type === "manual") {
        snapshotsRef.current.manual.set(entity.id, snapshotManual(entity as ManualEntity));
        return;
      }
      snapshotsRef.current.links.set(entity.id, snapshotLink(entity as LinkEntity));
    },
    []
  );

  const revertEntityToSnapshot = useCallback(
    (type: EntityKind, id: string): boolean => {
      const snapshots = snapshotsRef.current;
      if (type === "hub") {
        const snapshot = snapshots.hubs.get(id);
        if (!snapshot) {
          return false;
        }
        setModel((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            hubs: previous.hubs.map((hub) => (hub.id === id ? snapshotHub(snapshot) : hub))
          };
        });
        return true;
      }
      if (type === "satellite") {
        const snapshot = snapshots.satellites.get(id);
        if (!snapshot) {
          return false;
        }
        setModel((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            satellites: previous.satellites.map((satellite) =>
              satellite.id === id ? snapshotSatellite(snapshot) : satellite
            )
          };
        });
        return true;
      }
      const snapshot = snapshots.links.get(id);
      if (!snapshot) {
        return false;
      }
      setModel((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          links: previous.links.map((link) => (link.id === id ? snapshotLink(snapshot) : link))
        };
      });
      return true;
    },
    [setModel]
  );

  const loadWorkspaceModel = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }
    setIsMetadataLoading(true);
    setMetadataError(null);
    try {
      if (!isBrowser) {
        throw new Error("Data Vault modelling workspace requires a browser context.");
      }
      const environmentId = getScopeEnvironmentId();
      const customerId = getScopeCustomerId();

      if (!environmentId || !customerId) {
        throw new Error("Missing environment or customer context. Re-select an environment to continue.");
      }

      const relatedEntitiesResponse = await getDataVaultRelatedEntities(environmentId, customerId);
      if (!isMountedRef.current) {
        return;
      }

      const mockedEntities = (relatedEntitiesResponse ?? []) as DataVaultNode[];
      const layoutReadyEntities = prepareLayoutEntities(
        mockedEntities
      );

      const apiLayout = buildLayoutFromRelatedEntities(layoutReadyEntities);
      if (Object.keys(apiLayout).length) {
        const nextLayout = {
          ...savedLayoutRef.current,
          ...apiLayout
        };
        const nextSyncedLayout = {
          ...syncedLayoutRef.current,
          ...apiLayout
        };
        savedLayoutRef.current = nextLayout;
        syncedLayoutRef.current = nextSyncedLayout;
        dispatch(mergeWorkspaceLayout(nextLayout));
        dispatch(mergeWorkspaceSyncedLayout(nextSyncedLayout));
      }

      const dataVaultModel = buildModelFromRelatedEntities(layoutReadyEntities, {
        defaultDwTargetType: activeDwTargetType
      });
      setModel(dataVaultModel);
      snapshotsRef.current = {
        hubs: new Map(dataVaultModel.hubs.map((hub) => [hub.id, snapshotHub(hub)])),
        satellites: new Map(
          dataVaultModel.satellites.map((satellite) => [satellite.id, snapshotSatellite(satellite)])
        ),
        links: new Map(dataVaultModel.links.map((link) => [link.id, snapshotLink(link)])),
        manual: new Map(
          dataVaultModel.manual.map((manualEntity) => [manualEntity.id, snapshotManual(manualEntity)])
        )
      };
      setDirtyEntities(new Set());
      setSelectedLinkId(null);
      setSelectedNodeId(null);
    } catch (error) {
      console.error("Failed to load Data Vault metadata.", error);
      if (isMountedRef.current) {
        const message =
          error instanceof Error ? error.message : "Unable to load Data Vault metadata.";
        setMetadataError(message);
        showStatus("Unable to load Data Vault metadata from the API.");
        const emptyModel: DataVaultModel = { hubs: [], satellites: [], links: [], manual: [] };
        setModel(emptyModel);
        snapshotsRef.current = {
          hubs: new Map(),
          satellites: new Map(),
          links: new Map(),
          manual: new Map()
        };
        setDirtyEntities(new Set());
        setSelectedNodeId(null);
        setSelectedLinkId(null);
        setSelectedRelationshipEdgeId(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsMetadataLoading(false);
      }
    }
  }, [
    activeDwTargetType,
    setModel,
    setDirtyEntities,
    setSelectedLinkId,
    setSelectedNodeId,
    setSelectedRelationshipEdgeId,
    setMetadataError,
    showStatus,
    dispatch
  ]);

  useEffect(() => {
    void loadWorkspaceModel();
  }, [loadWorkspaceModel]);

  const handleRefreshWorkspace = useCallback(() => {
    if (isMetadataLoading) {
      return;
    }
    void loadWorkspaceModel();
  }, [isMetadataLoading, loadWorkspaceModel]);

  useEffect(() => {
    if (!model) {
      setNodes([]);
      setEdges([]);
      return;
    }
    setNodes((previousNodes) => {
      const { nodes: draftNodes, edges: draftEdges } = generateFlowElements(
        model,
        previousNodes as FlowNode[],
        savedLayoutRef.current,
        selectedLinkId,
        selectedNodeId,
        (id) => {
          setSelectedNodeId(id);
          setSelectedLinkId(null);
          setSelectedRelationshipEdgeId(null);
        },
        (linkId) => {
          setSelectedLinkId(linkId);
          setSelectedNodeId(null);
          setSelectedRelationshipEdgeId(null);
        }
      );
      setEdges(draftEdges);
      return mergeNodes(draftNodes, previousNodes as FlowNode[], savedLayoutRef.current);
    });
  }, [model, selectedLinkId, selectedNodeId, setEdges, setNodes]);

  useEffect(() => {
    if (!nodes.length) {
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      setSelectedRelationshipEdgeId(null);
      return;
    }
    if (selectedLinkId) {
      return;
    }
    if (!selectedNodeId) {
      return;
    }
    if (nodes.some((node) => node.id === selectedNodeId)) {
      return;
    }
    setSelectedNodeId(null);
  }, [nodes, selectedNodeId, selectedLinkId]);

  useEffect(() => {
    if (!selectedRelationshipEdgeId) {
      return;
    }
    if (edges.some((edge) => edge.id === selectedRelationshipEdgeId)) {
      return;
    }
    setSelectedRelationshipEdgeId(null);
  }, [edges, selectedRelationshipEdgeId]);

  useEffect(() => {
    if (!model) {
      setSelectedLinkId(null);
      return;
    }
    if (selectedLinkId && !model.links.some((link) => link.id === selectedLinkId)) {
      setSelectedLinkId(null);
    }
  }, [model, selectedLinkId]);

  useEffect(() => {
    if (!model) {
      return;
    }
    const persistedModelSubjectAreas = [
      ...model.hubs.map((hub) => hub.record.subject_area),
      ...model.links.map((link) => link.record.subject_area),
      ...model.satellites.map((satellite) => satellite.record.subject_area)
    ]
      .map((value) => normalizeSubjectAreaValue(value ?? ""))
      .filter((value): value is string => Boolean(value));

    if (!persistedModelSubjectAreas.length) {
      return;
    }

    setSubjectAreaOptions((previous) => {
      const merged = Array.from(new Set([...previous, ...persistedModelSubjectAreas]));
      if (merged.length === previous.length) {
        return previous;
      }
      return merged.sort((a, b) => a.localeCompare(b));
    });
  }, [model, normalizeSubjectAreaValue]);

  const availableSubjectAreasForForms = useMemo(() => {
    const fromModel = model
      ? [
          ...model.hubs.map((hub) => hub.record.subject_area),
          ...model.links.map((link) => link.record.subject_area),
          ...model.satellites.map((satellite) => satellite.record.subject_area)
        ]
      : [];
    return Array.from(
      new Set(
        [...subjectAreaOptions, ...fromModel]
          .map((value) => normalizeSubjectAreaValue(value ?? ""))
          .filter((value): value is string => Boolean(value))
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [model, normalizeSubjectAreaValue, subjectAreaOptions]);

  const registerSubjectArea = useCallback(async (value: string) => {
    const normalizedValue = normalizeSubjectAreaValue(value);
    if (!normalizedValue) {
      return {
        ok: false,
        value: "",
        error: "Enter a subject area name."
      };
    }

    const existingOption = availableSubjectAreasForForms.find(
      (option) => option.trim().toLowerCase() === normalizedValue.toLowerCase()
    );
    if (existingOption) {
      return {
        ok: true,
        value: existingOption
      };
    }

    setSubjectAreaOptions((previous) =>
      Array.from(new Set([...previous, normalizedValue])).sort((a, b) => a.localeCompare(b))
    );

    return {
      ok: true,
      value: normalizedValue
    };
  }, [availableSubjectAreasForForms, normalizeSubjectAreaValue]);

  const validationMessages = useMemo<ValidationMessage[]>(
    () => (model ? computeValidation(model) : []),
    [model]
  );

  useEffect(() => {
    setValidationVisible(false);
  }, [model]);

  const resolveNodeFilterValue = useCallback(
    (node: FlowNode, field: Exclude<VaultFilterField, "">): string => {
      const record = node.data?.entity?.record;
      if (!record) {
        return "";
      }
      if (field === "target_entity_rdv_name") {
        return String(record.target_entity_rdv_name ?? record.target_table ?? "").trim();
      }
      return String(record[field] ?? "").trim();
    },
    []
  );

  // Distinct values for the low-cardinality filter fields, so the filter offers a dropdown of real
  // subject areas / ensembles instead of error-prone free text. target_entity_rdv_name stays
  // free-text (high cardinality).
  const filterFieldOptions = useMemo<Record<"subject_area" | "ensemble", string[]>>(() => {
    const subjectAreas = new Set<string>();
    const ensembles = new Set<string>();
    const collect = (record: { subject_area?: string; ensemble?: string }) => {
      const sa = record.subject_area?.trim();
      const ens = record.ensemble?.trim();
      if (sa) subjectAreas.add(sa);
      if (ens) ensembles.add(ens);
    };
    model?.hubs.forEach((hub) => collect(hub.record));
    model?.satellites.forEach((satellite) => collect(satellite.record));
    model?.links.forEach((link) => collect(link.record));
    model?.manual.forEach((manual) => collect(manual.record));
    return {
      subject_area: Array.from(subjectAreas).sort((a, b) => a.localeCompare(b)),
      ensemble: Array.from(ensembles).sort((a, b) => a.localeCompare(b))
    };
  }, [model]);

  // Ensembles run to ~85 in a model, so the value is picked from a searchable list.
  const filterValueOptions = useMemo<SearchableSelectOption[]>(
    () =>
      filterField === "subject_area" || filterField === "ensemble"
        ? filterFieldOptions[filterField].map((option) => ({ label: option, value: option }))
        : [],
    [filterField, filterFieldOptions]
  );

  const filteredFlow = useMemo(() => {
    // 1. Field filter (unchanged behavior).
    let workingNodes = nodes;
    if (activeFilter) {
      const needle = activeFilter.value.trim();
      if (needle) {
        // EXACT for subject area and ensemble — those come from a dropdown of real values, and a
        // substring match meant picking `sa_customer` also returned `sa_customer_return`. The
        // entity-name field stays a substring search, because there it IS a search.
        workingNodes = nodes.filter((node) =>
          matchesCanvasFilter(
            resolveNodeFilterValue(node, activeFilter.field),
            needle,
            activeFilter.field
          )
        );
      }
    }

    // 2. Satellite count per hub (from the model, not the filtered node set).
    const satCountByHub = new Map<string, number>();
    model?.satellites.forEach((satellite) => {
      if (satellite.parentHubId) {
        satCountByHub.set(satellite.parentHubId, (satCountByHub.get(satellite.parentHubId) ?? 0) + 1);
      }
    });

    // 3. Hide satellites whose parent hub is collapsed; enrich hub nodes with collapse state.
    const decoratedNodes = workingNodes
      .filter((node) => {
        if (node.type !== "satelliteNode") {
          return true;
        }
        const parentHubId = (node.data as SatelliteNodeData).entity.parentHubId;
        return !(parentHubId && collapsedHubIds.has(parentHubId));
      })
      .map((node) => {
        if (node.type !== "hubNode") {
          return node;
        }
        const hubId = node.id;
        const count = satCountByHub.get(hubId) ?? 0;
        if (count === 0) {
          return node;
        }
        return {
          ...node,
          data: {
            ...(node.data as HubNodeData),
            satelliteCount: count,
            collapsed: collapsedHubIds.has(hubId),
            onToggleCollapse: handleToggleHubCollapse
          }
        };
      });

    const visibleIds = new Set(decoratedNodes.map((node) => node.id));
    const filteredEdges = edges.filter(
      (edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)
    );

    return { nodes: decoratedNodes, edges: filteredEdges };
  }, [activeFilter, edges, nodes, resolveNodeFilterValue, model, collapsedHubIds, handleToggleHubCollapse]);

  const handleFilterApply = useCallback(() => {
    if (!filterField) {
      setActiveFilter(null);
      setFilterPanelOpen(false);
      return;
    }
    const value = filterValueDraft.trim();
    if (!value) {
      setActiveFilter(null);
      setFilterPanelOpen(false);
      return;
    }
    setActiveFilter({ field: filterField, value });
    setFilterPanelOpen(false);
  }, [filterField, filterValueDraft]);

  const handleFilterClear = useCallback(() => {
    setFilterField("");
    setFilterValueDraft("");
    setActiveFilter(null);
  }, []);

  const nodeSearchNormalized = nodeSearchQuery.trim().toLowerCase();

  const nodeSearchMatch = useMemo(() => {
    if (!nodeSearchNormalized) {
      return {
        matchedNodeIds: new Set<string>(),
        matchedNodeList: [] as string[]
      };
    }

    const matchedNodeList = filteredFlow.nodes
      .filter((node) => {
        return resolveNodeFilterValue(node, "target_entity_rdv_name")
          .toLowerCase()
          .includes(nodeSearchNormalized);
      })
      .map((node) => node.id);

    return {
      matchedNodeIds: new Set<string>(matchedNodeList),
      matchedNodeList
    };
  }, [filteredFlow.nodes, nodeSearchNormalized, resolveNodeFilterValue]);

  const selectedRelationshipEdge = useMemo(
    () => edges.find((edge) => edge.id === selectedRelationshipEdgeId) ?? null,
    [edges, selectedRelationshipEdgeId]
  );

  const highlightedFlow = useMemo(() => {
    const highlightedEdgeIds = new Set<string>();
    if (nodeSearchNormalized) {
      filteredFlow.edges.forEach((edge) => {
        if (
          nodeSearchMatch.matchedNodeIds.has(edge.source) &&
          nodeSearchMatch.matchedNodeIds.has(edge.target)
        ) {
          highlightedEdgeIds.add(edge.id);
        }
      });
    }

    const highlightedNodes = filteredFlow.nodes.map((node) => {
      const isMatch = !nodeSearchNormalized || nodeSearchMatch.matchedNodeIds.has(node.id);
      return {
        ...node,
        style: {
          ...(node.style ?? {}),
          opacity: isMatch ? 1 : 0.12
        }
      };
    });

    const highlightedEdges = filteredFlow.edges.map((edge) => {
      const isMatch = !nodeSearchNormalized || highlightedEdgeIds.has(edge.id);
      const isSelected = edge.id === selectedRelationshipEdgeId;
      return {
        ...edge,
        animated: isSelected || Boolean(edge.animated),
        selected: isSelected,
        style: {
          ...(edge.style ?? {}),
          filter: isSelected ? "drop-shadow(0 0 8px rgba(15, 23, 42, 0.35))" : edge.style?.filter,
          opacity: isMatch || isSelected ? 1 : 0.08,
          strokeWidth: isSelected ? 4 : edge.style?.strokeWidth
        }
      };
    });

    return {
      nodes: highlightedNodes,
      edges: highlightedEdges
    };
  }, [filteredFlow, nodeSearchMatch.matchedNodeIds, nodeSearchNormalized, selectedRelationshipEdgeId]);

  useEffect(() => {
    if (!nodeSearchNormalized || nodeSearchMatch.matchedNodeList.length === 0) {
      searchFocusKeyRef.current = "";
      return;
    }
    const instance = reactFlowInstanceRef.current;
    if (!instance) {
      return;
    }
    const focusKey = `${nodeSearchNormalized}:${nodeSearchMatch.matchedNodeList.join("|")}`;
    if (focusKey === searchFocusKeyRef.current) {
      return;
    }
    searchFocusKeyRef.current = focusKey;
    const focusNodeId = nodeSearchMatch.matchedNodeList[0];
    void instance.fitView({
      nodes: [{ id: focusNodeId }],
      padding: FIT_VIEW_PADDING,
      duration: 600,
      maxZoom: 1.4
    });
  }, [nodeSearchMatch.matchedNodeList, nodeSearchNormalized]);

  useEffect(() => {
    const nodeId = pendingCreatedNodeFocusRef.current;
    if (!nodeId || !highlightedFlow.nodes.some((node) => node.id === nodeId)) {
      return;
    }
    const instance = reactFlowInstanceRef.current;
    if (!instance) {
      return;
    }
    pendingCreatedNodeFocusRef.current = null;
    const focusCreatedNode = (remainingRetries: number) => {
      const node = instance.getNode(nodeId);
      const position = node?.positionAbsolute ?? node?.position;
      if (!position) {
        if (remainingRetries > 0) {
          window.requestAnimationFrame(() => focusCreatedNode(remainingRetries - 1));
        }
        return;
      }
      const width = node?.width ?? LAYOUT_NODE_WIDTH;
      const height = node?.height ?? LAYOUT_NODE_HEIGHT;
      void instance.setCenter(position.x + width / 2, position.y + height / 2, {
        zoom: CREATED_NODE_FOCUS_ZOOM,
        duration: 650
      });
    };
    window.requestAnimationFrame(() => focusCreatedNode(CREATED_NODE_FOCUS_RETRIES));
  }, [highlightedFlow.nodes]);

  useEffect(() => {
    if (!activeFilter || !selectedNodeId) {
      return;
    }
    if (filteredFlow.nodes.some((node) => node.id === selectedNodeId)) {
      return;
    }
    setSelectedNodeId(null);
    setDetailsPanelOpen(false);
  }, [activeFilter, filteredFlow.nodes, selectedNodeId]);

const selectedEntity: SelectedEntity = useMemo(() => {
   if (!model) {
     return null;
    }
    if (selectedLinkId) {
      const link = model.links.find((candidate) => candidate.id === selectedLinkId);
      if (link) {
        return { type: "link", entity: link };
      }
    }
    if (selectedNodeId) {
      const resolved = getEntityById(model, selectedNodeId);
      if (resolved) {
        return resolved as SelectedEntity;
      }
    }
    return null;
  }, [model, selectedLinkId, selectedNodeId]);

  useEffect(() => {
    if (!selectedEntity) {
      setDetailsPanelOpen(false);
    }
  }, [selectedEntity]);

  const selectedEntityChip = useMemo(() => {
    if (!selectedEntity) {
      return null;
    }
    const baseClasses =
      "tw-inline-flex tw-items-center tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold";
    if (selectedEntity.type === "hub") {
      return { label: "Hub", className: `${baseClasses} tw-bg-blue-100 tw-text-blue-700` };
    }
    if (selectedEntity.type === "satellite") {
      return {
        label: "Satellite",
        className: `${baseClasses} tw-bg-rose-100 tw-text-rose-700`
      };
    }
    if (selectedEntity.type === "link") {
      return { label: "Link", className: `${baseClasses} tw-bg-sky-100 tw-text-sky-700` };
    }
    if (selectedEntity.type === "manual") {
      return {
        label: "Manual",
        className: `${baseClasses} tw-bg-slate-200 tw-text-slate-700`
      };
    }
    return null;
  }, [selectedEntity]);

  const currentColumns =
    selectedEntity &&
    (selectedEntity.type === "hub" ||
      selectedEntity.type === "satellite" ||
      selectedEntity.type === "link")
      ? selectedEntity.entity.columns
      : [];

  const currentTableMappings = selectedEntity ? selectedEntity.entity.mappings : [];
  const selectedEntityKey = selectedEntity
    ? buildEntityKey(selectedEntity.type, selectedEntity.entity.id)
    : null;
  const selectedEntityIsDirty = isSelectedEntityDirtyAgainstSnapshot(
    selectedEntity,
    snapshotsRef.current
  );
  const hasUnsavedChanges = detailsPanelOpen && selectedEntityIsDirty;

  useEffect(() => {
    setSaveError(null);
    setSaveSuccess(null);
  }, [selectedEntityKey]);

  const extractApiErrorMessage = useCallback((error: unknown): string => {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (typeof error === "object" && error !== null) {
      const errorLike = error as {
        detail?: string;
        message?: string;
        resBody?: { detail?: string; message?: string };
        body?: { detail?: string; message?: string };
      };
      return (
        errorLike.resBody?.detail ||
        errorLike.resBody?.message ||
        errorLike.body?.detail ||
        errorLike.body?.message ||
        errorLike.detail ||
        errorLike.message ||
        "Failed to save entity"
      );
    }
    return "Failed to save entity";
  }, []);

  const handleHubFieldChange = useCallback(
    (hubId: string, field: keyof TableRecord, value: string) => {
      const currentValue =
        model?.hubs.find((hub) => hub.id === hubId)?.record[field];
      if (
        normalizeComparableString(currentValue as string | null | undefined) ===
        normalizeComparableString(value)
      ) {
        return;
      }
      setSaveSuccess(null);
      updateHubField(hubId, field, value);
      markEntityDirty("hub", hubId);
    },
    [markEntityDirty, model, updateHubField]
  );

  const handleSatelliteFieldChange = useCallback(
    (satelliteId: string, field: keyof TableRecord, value: string) => {
      const currentValue =
        model?.satellites.find((satellite) => satellite.id === satelliteId)?.record[field];
      if (
        normalizeComparableString(currentValue as string | null | undefined) ===
        normalizeComparableString(value)
      ) {
        return;
      }
      setSaveSuccess(null);
      updateSatelliteField(satelliteId, field, value);
      markEntityDirty("satellite", satelliteId);
    },
    [markEntityDirty, model, updateSatelliteField]
  );

  const handleLinkFieldChange = useCallback(
    (linkId: string, field: keyof TableRecord, value: string) => {
      const currentValue =
        model?.links.find((link) => link.id === linkId)?.record[field];
      if (
        normalizeComparableString(currentValue as string | null | undefined) ===
        normalizeComparableString(value)
      ) {
        return;
      }
      setSaveSuccess(null);
      updateLinkField(linkId, field, value);
      markEntityDirty("link", linkId);
    },
    [markEntityDirty, model, updateLinkField]
  );

  const handleManualFieldChange = useCallback(
    (manualId: string, field: keyof TableRecord, value: string) => {
      const currentValue =
        model?.manual.find((manualEntity) => manualEntity.id === manualId)?.record[field];
      if (
        normalizeComparableString(currentValue as string | null | undefined) ===
        normalizeComparableString(value)
      ) {
        return;
      }
      setSaveSuccess(null);
      updateManualField(manualId, field, value);
      markEntityDirty("manual", manualId);
    },
    [markEntityDirty, model, updateManualField]
  );

  const handleSatelliteParentChange = useCallback(
    (satelliteId: string, parentHubId: string | null) => {
      const currentParentHubId =
        model?.satellites.find((satellite) => satellite.id === satelliteId)?.parentHubId ?? null;
      if (currentParentHubId === parentHubId) {
        return;
      }
      setSaveSuccess(null);
      updateSatelliteParent(satelliteId, parentHubId);
      markEntityDirty("satellite", satelliteId);
    },
    [markEntityDirty, model, updateSatelliteParent]
  );

  const handleLinkHubsChange = useCallback(
    (linkId: string, hubIds: string[]) => {
      const current = model?.links.find((link) => link.id === linkId)?.connectedHubIds ?? [];
      if (current.length === hubIds.length && current.every((id, index) => id === hubIds[index])) {
        return;
      }
      setSaveSuccess(null);
      updateLinkHubs(linkId, hubIds);
      markEntityDirty("link", linkId);
    },
    [markEntityDirty, model, updateLinkHubs]
  );

  const validateEntity = useCallback(
    (entity: SelectedEntity | null): string[] => {
      if (!entity) {
        return ["Select an entity to save."];
      }
      const record = entity.entity.record;
      const errors: string[] = [];

      if (!record.target_table) {
        errors.push("Target table is required");
      }
      if (!record.subject_area) {
        errors.push("Subject area is required");
      }
      if (!record.ensemble) {
        errors.push("Ensemble is required");
      }
      if (!record.data_source_name) {
        errors.push("Data source name is required");
      }
      if (!record.source_entity_name) {
        errors.push("Source table is required");
      }
      if (!record.target_schema) {
        errors.push("Target schema is required");
      }
      // Only enforceable where distribution exists at all (dedicated SQL pool); the
      // backend rejects the same combination, this keeps it out of a failed request.
      if (isDistributionEditable(record, activeDwTargetType)) {
        const distributionError = getDistributionValidationError(
          record.distribution_type,
          record.distribution_key
        );
        if (distributionError) {
          errors.push(distributionError);
        }
      }

      if (entity.type === "link") {
        if (entity.entity.connectedHubIds.length < 2) {
          errors.push("Link must connect at least two hubs");
        }
        const missingHubIds = (entity.entity.linkedHubs ?? []).some(
          (hub) => !hub.hub_target_entity_rdv_id
        );
        if (missingHubIds) {
          errors.push("Connected hubs must be saved before saving link");
        }
      }

      return errors;
    },
    [activeDwTargetType]
  );

  const selectedEntityValidationErrors = useMemo(
    () => (selectedEntity ? validateEntity(selectedEntity) : []),
    [selectedEntity, validateEntity]
  );
  const canSaveSelectedEntity =
    selectedEntityIsDirty && selectedEntityValidationErrors.length === 0;
  const isSelectedEntitySaving =
    Boolean(selectedEntity) && savingEntityId === selectedEntity?.entity.id;
  const handleAddHub = useCallback(() => {
    const createdId = addHub();
    if (createdId) {
      reserveCreatedNodePosition(createdId);
      pendingCreatedNodeFocusRef.current = createdId;
      setActiveFilter(null);
      setSelectedNodeId(createdId);
      setSelectedLinkId(null);
      markEntityDirty("hub", createdId);
    }
  }, [addHub, markEntityDirty, reserveCreatedNodePosition]);

  const handleAddSatellite = useCallback(() => {
    const createdId = addSatellite();
    if (createdId) {
      reserveCreatedNodePosition(createdId);
      pendingCreatedNodeFocusRef.current = createdId;
      setActiveFilter(null);
      setSelectedNodeId(createdId);
      setSelectedLinkId(null);
      markEntityDirty("satellite", createdId);
    }
  }, [addSatellite, markEntityDirty, reserveCreatedNodePosition]);

  const handleAddLink = useCallback(() => {
    const createdId = addLink();
    if (createdId) {
      reserveCreatedNodePosition(createdId);
      pendingCreatedNodeFocusRef.current = createdId;
      setActiveFilter(null);
      setSelectedLinkId(createdId);
      setSelectedNodeId(null);
      markEntityDirty("link", createdId);
    }
  }, [addLink, markEntityDirty, reserveCreatedNodePosition]);

  const handleOpenPlayModal = useCallback(() => {
    const defaultArea =
      playSubjectArea ||
      availableSubjectAreasForForms[0] ||
      "";
    const resolved = defaultArea || "";
    setPlaySubjectArea(resolved);
    setPlayMode("model");
    setPlaySqlPreview("");
    setPlaySqlError(null);
    setPlaySqlWarning(null);
    setIsPlaySqlStale(false);
    setPlaySqlCopyStatus("idle");
    setPlayModalOpen(true);
  }, [availableSubjectAreasForForms, playSubjectArea]);

  const handlePlaySubjectAreaChange = useCallback((value: string) => {
    setPlaySubjectArea(value);
    setPlaySqlError(null);
    setPlaySqlWarning(null);
    setPlaySqlCopyStatus("idle");
    setIsPlaySqlStale(Boolean(playSqlPreview));
  }, [playSqlPreview]);

  // Switching Model <-> DQ clears the previous preview (it belongs to the other mode).
  const handlePlayModeChange = useCallback((mode: "model" | "dq") => {
    setPlayMode((current) => {
      if (current === mode) {
        return current;
      }
      setPlaySqlPreview("");
      setPlaySqlError(null);
      setPlaySqlWarning(null);
      setPlayGenerationReport(null);
      setPlaySqlCopyStatus("idle");
      setIsPlaySqlStale(false);
      return mode;
    });
  }, []);

  const handleGeneratePlaySqlPreview = useCallback(async () => {
    const subjectArea = playSubjectArea.trim();
    if (!subjectArea) {
      setPlaySqlError("Select a subject area before generating SQL preview.");
      return;
    }
    const { environmentId } = getWorkspaceContextIds();
    setIsPlaySqlLoading(true);
    setPlaySqlError(null);
    setPlaySqlWarning(null);
    setPlayGenerationReport(null);
    setPlaySqlCopyStatus("idle");
    try {
      const response =
        playMode === "dq"
          ? await generateDqTestRawDataVaultSqlPreview(environmentId, subjectArea)
          : await generateRawDataVaultSqlPreview(environmentId, subjectArea, null, {
              shouldGenerateInserts: playGenerateInserts,
              fileSystem: isLakehouseTarget ? playFileSystem : null
            });
      const sql = response.sql ?? "";
      setPlaySqlPreview(sql);
      setPlayGenerationReport(
        playMode === "dq"
          ? null
          : ((response as RawDataVaultSqlPreviewResponse).generation_report ?? null)
      );
      setIsPlaySqlStale(false);
      if (!sql.trim()) {
        setPlaySqlWarning(
          playMode === "dq"
            ? "No DQ test SQL was generated for this subject area."
            : "No SQL was generated. Check source mappings/column mappings for this subject area."
        );
      }
    } catch (error) {
      setPlaySqlError(
        getApiErrorMessage(
          error,
          playMode === "dq"
            ? "Unable to generate DQ test preview."
            : "Unable to generate SQL preview."
        )
      );
    } finally {
      setIsPlaySqlLoading(false);
    }
  }, [playSubjectArea, playMode, playGenerateInserts, playFileSystem, isLakehouseTarget]);

  const handleCopyPlaySqlPreview = useCallback(async () => {
    if (!playSqlPreview || isPlaySqlLoading) {
      return;
    }
    if (playSqlCopyFeedbackTimeoutRef.current) {
      window.clearTimeout(playSqlCopyFeedbackTimeoutRef.current);
      playSqlCopyFeedbackTimeoutRef.current = undefined;
    }
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(playSqlPreview);
      setPlaySqlCopyStatus("success");
    } catch (error) {
      console.error("Unable to copy SQL preview", error);
      setPlaySqlCopyStatus("error");
    } finally {
      playSqlCopyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setPlaySqlCopyStatus("idle");
        playSqlCopyFeedbackTimeoutRef.current = undefined;
      }, 2000);
    }
  }, [isPlaySqlLoading, playSqlPreview]);

  const handleClosePlayModal = useCallback(() => {
    setPlayModalOpen(false);
  }, []);

  const handleSaveSelectedEntity = useCallback(async () => {
    if (!selectedEntity) {
      showStatus("Select an entity to save.");
      return;
    }
    setSaveSuccess(null);
    const errors = validateEntity(selectedEntity);
    if (errors.length) {
      setSaveError(errors.join(", "));
      showStatus(errors[0]);
      return;
    }
    const environmentId = typeof window !== "undefined" ? getScopeEnvironmentId() : null;
    const customerId = typeof window !== "undefined" ? getScopeCustomerId() : null;
    if (!environmentId || !customerId) {
      setSaveError("Missing environment or customer context");
      showStatus("Missing environment or customer context.");
      return;
    }
    const entityType = selectedEntity.type;
    const entityId = selectedEntity.entity.id;
    const record = selectedEntity.entity.record;
    const hasPersistedRecord = Boolean(record.target_entity_rdv_id);
    setSavingEntityId(entityId);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const parentHubRecord =
        entityType === "satellite" && selectedEntity.entity.parentHubId
          ? model?.hubs.find((hub) => hub.id === selectedEntity.entity.parentHubId) ?? null
          : null;
      const payload = buildPayloadFromEntity(selectedEntity.entity, entityType, {
        allHubs: model?.hubs ?? [],
        parentHub: parentHubRecord,
        position: getNodePosition(entityId),
        includeDistribution: isDistributionEditable(record, activeDwTargetType)
      });
      const response = hasPersistedRecord
        ? await updateDataVaultMappings([payload], environmentId, customerId)
        : await createDataVaultMappings([payload], environmentId, customerId);
      const result = response.results?.[0];
      if (result) {
        applyEntityRecordUpdates(entityType, entityId, {
          source_entity_id: result.source_entity_id ? String(result.source_entity_id) : undefined,
          target_entity_rdv_id: result.target_entity_rdv_id
            ? String(result.target_entity_rdv_id)
            : undefined
        });
      }
      markEntitySaved(entityType, entityId);
      updateSnapshotForEntity(entityType, {
        ...selectedEntity.entity,
        record: {
          ...selectedEntity.entity.record,
          source_entity_id: result?.source_entity_id ? String(result.source_entity_id) : selectedEntity.entity.record.source_entity_id,
          target_entity_rdv_id: result?.target_entity_rdv_id
            ? String(result.target_entity_rdv_id)
            : selectedEntity.entity.record.target_entity_rdv_id
        }
      });
      const successMessage =
        result?.message?.trim() ||
        (hasPersistedRecord ? "Entity updated successfully." : "Entity saved successfully.");
      setSaveSuccess(successMessage);
      showStatus(successMessage);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      setSaveError(message);
      setSaveSuccess(null);
      showStatus(message);
    } finally {
      setSavingEntityId(null);
    }
  }, [
    activeDwTargetType,
    applyEntityRecordUpdates,
    extractApiErrorMessage,
    markEntitySaved,
    model,
    getNodePosition,
    selectedEntity,
    showStatus,
    updateSnapshotForEntity,
    validateEntity
  ]);

  const handleCancelChanges = useCallback(() => {
    if (!selectedEntity) {
      return;
    }
    setSaveError(null);
    setSaveSuccess(null);
    const { type, entity } = selectedEntity;
    const snapshotExists =
      type === "hub"
        ? snapshotsRef.current.hubs.has(entity.id)
        : type === "satellite"
        ? snapshotsRef.current.satellites.has(entity.id)
        : snapshotsRef.current.links.has(entity.id);
    if (!snapshotExists) {
      if (type === "hub") {
        removeHub(entity.id);
      } else if (type === "satellite") {
        removeSatellite(entity.id);
      } else {
        removeLink(entity.id);
      }
      markEntitySaved(type, entity.id);
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      showStatus("Draft entity removed.");
      return;
    }
    const reverted = revertEntityToSnapshot(type, entity.id);
    if (reverted) {
      markEntitySaved(type, entity.id);
      showStatus("Changes reverted.");
    } else {
      showStatus("Unable to revert changes.");
    }
  }, [
    markEntitySaved,
    removeHub,
    removeLink,
    removeSatellite,
    revertEntityToSnapshot,
    selectedEntity,
    showStatus
  ]);

  const handleCloseDetailsPanel = useCallback(() => {
    if (selectedEntityIsDirty) {
      const shouldDiscard = window.confirm(
        "Discard unsaved changes and close this details panel?"
      );
      if (!shouldDiscard) {
        return;
      }
      handleCancelChanges();
    }
    setDetailsPanelOpen(false);
  }, [handleCancelChanges, selectedEntityIsDirty]);

  const handleRemoveHub = useCallback(
    async (hubId: string) => {
      setSaveError(null);
      const targetHub = model?.hubs.find((hub) => hub.id === hubId);
      const associatedSatelliteIds =
        model?.satellites
          .filter((satellite) => satellite.parentHubId === hubId)
          .map((satellite) => satellite.id) ?? [];
      const targetEntityId = parseNumericId(targetHub?.record.target_entity_rdv_id);
      const shouldSync = Boolean(targetEntityId);
      if (shouldSync) {
        const displayName =
          targetHub?.record.target_table?.trim() || targetHub?.record.ensemble || "Hub";
        setDeleteOverlay({ type: "hub", name: displayName });
        const { environmentId, customerId } = getWorkspaceContextIds();
        try {
          await deleteTargetEntityRdvNode(targetEntityId, environmentId, customerId);
        } catch (error) {
          setDeleteOverlay(null);
          const message =
            error instanceof Error ? error.message : "Unable to delete hub at this time.";
          setSaveError(message);
          showStatus("Hub deletion failed.");
          return;
        }
      }
      removeHub(hubId);
      snapshotsRef.current.hubs.delete(hubId);
      associatedSatelliteIds.forEach((satelliteId) => {
        snapshotsRef.current.satellites.delete(satelliteId);
        markEntitySaved("satellite", satelliteId);
      });
      markEntitySaved("hub", hubId);
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      if (shouldSync) {
        try {
          await loadWorkspaceModel();
        } finally {
          setDeleteOverlay(null);
        }
      }
    },
    [loadWorkspaceModel, markEntitySaved, model, removeHub, showStatus]
  );

  const handleRemoveSatellite = useCallback(
    async (satelliteId: string) => {
      setSaveError(null);
      const targetSatellite = model?.satellites.find((satellite) => satellite.id === satelliteId);
      const targetEntityId = parseNumericId(targetSatellite?.record.target_entity_rdv_id);
      const shouldSync = Boolean(targetEntityId);
      if (shouldSync) {
        const displayName =
          targetSatellite?.record.target_table?.trim() ||
          targetSatellite?.record.ensemble ||
          "Satellite";
        setDeleteOverlay({ type: "satellite", name: displayName });
        const { environmentId, customerId } = getWorkspaceContextIds();
        try {
          await deleteTargetEntityRdvNode(targetEntityId, environmentId, customerId);
        } catch (error) {
          setDeleteOverlay(null);
          const message =
            error instanceof Error
              ? error.message
              : "Unable to delete satellite at this time.";
          setSaveError(message);
          showStatus("Satellite deletion failed.");
          return;
        }
      }
      removeSatellite(satelliteId);
      snapshotsRef.current.satellites.delete(satelliteId);
      markEntitySaved("satellite", satelliteId);
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      if (shouldSync) {
        try {
          await loadWorkspaceModel();
        } finally {
          setDeleteOverlay(null);
        }
      }
    },
    [loadWorkspaceModel, markEntitySaved, model, removeSatellite, showStatus]
  );

  const handleRemoveLink = useCallback(
    async (linkId: string) => {
      setSaveError(null);
      const targetLink = model?.links.find((link) => link.id === linkId);
      const targetEntityId = parseNumericId(targetLink?.record.target_entity_rdv_id);
      const shouldSync = Boolean(targetEntityId);
      if (shouldSync) {
        const displayName =
          targetLink?.record.target_table?.trim() ||
          targetLink?.record.ensemble ||
          "Link";
        setDeleteOverlay({ type: "link", name: displayName });
        const { environmentId, customerId } = getWorkspaceContextIds();
        try {
          await deleteTargetEntityRdvNode(targetEntityId, environmentId, customerId);
        } catch (error) {
          setDeleteOverlay(null);
          const message =
            error instanceof Error ? error.message : "Unable to delete link at this time.";
          setSaveError(message);
          showStatus("Link deletion failed.");
          return;
        }
      }
      removeLink(linkId);
      snapshotsRef.current.links.delete(linkId);
      markEntitySaved("link", linkId);
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      if (shouldSync) {
        try {
          await loadWorkspaceModel();
        } finally {
          setDeleteOverlay(null);
        }
      }
    },
    [loadWorkspaceModel, markEntitySaved, model, removeLink, showStatus]
  );

  // Manual entities are legacy/migrated RDV rows (table_construct='MANUAL') that aren't generated.
  // They couldn't be removed before; deleting one uses the same construct-agnostic RDV-node delete.
  const handleRemoveManual = useCallback(
    async (manualId: string) => {
      setSaveError(null);
      const targetManual = model?.manual.find((entity) => entity.id === manualId);
      const targetEntityId = parseNumericId(targetManual?.record.target_entity_rdv_id);
      const shouldSync = Boolean(targetEntityId);
      if (shouldSync) {
        const displayName =
          targetManual?.record.target_table?.trim() ||
          targetManual?.record.target_entity_rdv_name ||
          "Entity";
        setDeleteOverlay({ type: "manual", name: displayName });
        const { environmentId, customerId } = getWorkspaceContextIds();
        try {
          await deleteTargetEntityRdvNode(targetEntityId, environmentId, customerId);
        } catch (error) {
          setDeleteOverlay(null);
          const message =
            error instanceof Error ? error.message : "Unable to delete entity at this time.";
          setSaveError(message);
          showStatus("Entity deletion failed.");
          return;
        }
      }
      removeManual(manualId);
      snapshotsRef.current.manual.delete(manualId);
      markEntitySaved("manual", manualId);
      setSelectedNodeId(null);
      setSelectedLinkId(null);
      if (shouldSync) {
        try {
          await loadWorkspaceModel();
        } finally {
          setDeleteOverlay(null);
        }
      }
    },
    [loadWorkspaceModel, markEntitySaved, model, removeManual, showStatus]
  );

  const handleSaveModel = useCallback(() => {
    if (!model) {
      return;
    }
    const payload = {
      model: {
        hubs: model.hubs,
        satellites: model.satellites,
        links: model.links
      },
      metadata: {
        exported: new Date().toISOString(),
        version: "1.0"
      }
    };
    downloadFile(
      `data-vault-model-${Date.now()}.json`,
      JSON.stringify(payload, null, 2),
      "application/json"
    );
    showStatus("Model exported as JSON.");
  }, [model, showStatus]);

  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // const handleValidate = useCallback(() => {
  //   setValidationVisible(true);
  //   if (!validationMessages.length) {
  //     showStatus("Model passes all expert validations.");
  //   } else {
  //     showStatus(validationMessages[0].message);
  //   }
  // }, [showStatus, validationMessages]);

  const handleSaveLayout = useCallback(() => {
    if (!model) {
      return;
    }
    const layout = nodes.reduce<LayoutMap>((acc, node) => {
      if (node.position) {
        acc[node.id] = node.position;
      }
      return acc;
    }, {} as LayoutMap);
    if (!Object.keys(layout).length) {
      showStatus("No layout changes to save.");
      return;
    }
    savedLayoutRef.current = {
      ...savedLayoutRef.current,
      ...layout
    };
    dispatch(mergeWorkspaceLayout(layout));
    const layoutEntries = buildLayoutSyncPayload(
      model,
      layout,
      syncedLayoutRef.current
    );
    if (!layoutEntries.length) {
      showStatus("No layout changes to save.");
      return;
    }
    const { environmentId, customerId } = getWorkspaceContextIds();
    void saveWorkspaceLayout(layoutEntries, environmentId, customerId)
      .then(() => {
        const syncedDelta = buildSyncedLayoutDelta(model, layoutEntries, layout);
        syncedLayoutRef.current = {
          ...syncedLayoutRef.current,
          ...syncedDelta
        };
        dispatch(mergeWorkspaceSyncedLayout(syncedDelta));
        showStatus("Layout saved.");
      })
      .catch((error) => {
        console.error("Failed to sync layout positions.", error);
        showStatus("Unable to save layout.");
      });
  }, [dispatch, model, nodes, showStatus]);

  const handleAutoArrangeLayout = useCallback(() => {
    if (!model) {
      return;
    }
    // Auto Arrange recomputes every position from scratch and there is no undo — so confirm before
    // it replaces a layout the user has changed (don't nag when nothing is dirty).
    if (
      hasUnsavedLayoutChanges &&
      !window.confirm(
        "Auto Arrange will replace the current arrangement and cannot be undone. Continue?"
      )
    ) {
      return;
    }
    const nextLayout = buildAutoArrangeLayout(model);
    if (!Object.keys(nextLayout).length) {
      showStatus("No nodes to arrange.");
      return;
    }
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const position = nextLayout[node.id];
        return position ? { ...node, position } : node;
      })
    );
    showStatus("Canvas arranged. Save layout to persist.");
    window.setTimeout(() => {
      void reactFlowInstanceRef.current?.fitView({
        padding: FIT_VIEW_PADDING,
        duration: 650,
        maxZoom: 1.15
      });
    }, 50);
  }, [model, setNodes, showStatus, hasUnsavedLayoutChanges]);

  const handleNodesChange = useCallback(
    (changes: Parameters<typeof applyNodeChanges>[0]) => {
      setNodes((nds) => {
        const updated = applyNodeChanges(changes, nds);
        const layout = updated.reduce<LayoutMap>((acc, node) => {
          acc[node.id] = node.position;
          return acc;
        }, {} as LayoutMap);
        savedLayoutRef.current = {
          ...savedLayoutRef.current,
          ...layout
        };
        return updated;
      });
    },
    [setNodes]
  );

  const handleNodeDragStop = useCallback(
    (_: React.MouseEvent, node: FlowNode) => {
      if (!node.position) {
        return;
      }
      const singleNodeLayout: LayoutMap = {
        [node.id]: {
          x: node.position.x,
          y: node.position.y
        }
      };
      savedLayoutRef.current = {
        ...savedLayoutRef.current,
        ...singleNodeLayout
      };
      dispatch(mergeWorkspaceLayout(singleNodeLayout));
    },
    [dispatch]
  );

  const removeRelationshipFromLocalModel = useCallback(
    (edge: FlowEdge) => {
      setModel((previous) => {
        if (!previous || !edge.data) {
          return previous;
        }

        if (edge.data.relationshipType === "hub-satellite") {
          return {
            ...previous,
            satellites: previous.satellites.map((satellite) =>
              satellite.id === edge.target
                ? {
                    ...satellite,
                    parentHubId: null,
                    satMemberId: null
                  }
                : satellite
            )
          };
        }

        if (edge.data.relationshipType === "link-hub") {
          return {
            ...previous,
            links: previous.links.map((link) => {
              if (link.id !== edge.source) {
                return link;
              }
              const nextMemberIds = { ...(link.linkMemberIdsByHubId ?? {}) };
              delete nextMemberIds[edge.target];
              return {
                ...link,
                connectedHubIds: link.connectedHubIds.filter((hubId) => hubId !== edge.target),
                linkedHubs: (link.linkedHubs ?? []).filter((linkedHub) => {
                  const matchesByCanvasId = linkedHub.hub_target_entity_rdv_name === edge.target;
                  const matchesByTargetId = linkedHub.hub_target_entity_rdv_id
                    ? String(linkedHub.hub_target_entity_rdv_id) === edge.target
                    : false;
                  return !(matchesByCanvasId || matchesByTargetId);
                }),
                linkMemberIdsByHubId: nextMemberIds
              };
            })
          };
        }

        if (edge.data.relationshipType === "link-satellite") {
          return {
            ...previous,
            links: previous.links.map((link) =>
              link.id === edge.source
                ? {
                    ...link,
                    satelliteIds: link.satelliteIds.filter(
                      (satelliteId) => satelliteId !== edge.target
                    )
                  }
                : link
            )
          };
        }

        return previous;
      });
    },
    [setModel]
  );

  const removeRelationshipFromSnapshots = useCallback((edge: FlowEdge) => {
    if (!edge.data) {
      return;
    }

    if (edge.data.relationshipType === "hub-satellite") {
      const satellite = snapshotsRef.current.satellites.get(edge.target);
      if (satellite) {
        snapshotsRef.current.satellites.set(edge.target, {
          ...satellite,
          parentHubId: null,
          satMemberId: null
        });
      }
      return;
    }

    if (edge.data.relationshipType === "link-hub") {
      const link = snapshotsRef.current.links.get(edge.source);
      if (!link) {
        return;
      }
      const nextMemberIds = { ...(link.linkMemberIdsByHubId ?? {}) };
      delete nextMemberIds[edge.target];
      snapshotsRef.current.links.set(edge.source, {
        ...link,
        connectedHubIds: link.connectedHubIds.filter((hubId) => hubId !== edge.target),
        linkedHubs: (link.linkedHubs ?? []).filter((linkedHub) => {
          const matchesByCanvasId = linkedHub.hub_target_entity_rdv_name === edge.target;
          const matchesByTargetId = linkedHub.hub_target_entity_rdv_id
            ? String(linkedHub.hub_target_entity_rdv_id) === edge.target
            : false;
          return !(matchesByCanvasId || matchesByTargetId);
        }),
        linkMemberIdsByHubId: nextMemberIds
      });
      return;
    }

    const link = snapshotsRef.current.links.get(edge.source);
    if (!link) {
      return;
    }
    snapshotsRef.current.links.set(edge.source, {
      ...link,
      satelliteIds: link.satelliteIds.filter((satelliteId) => satelliteId !== edge.target)
    });
  }, []);

  const handleDeleteRelationshipEdge = useCallback(
    async (edge: FlowEdge | null) => {
      if (!edge?.data) {
        showStatus("Select a relationship line to delete.");
        return;
      }

      const { environmentId, customerId } = getWorkspaceContextIds();
      setDeletingRelationshipEdgeId(edge.id);
      try {
        if (edge.data.relationshipType === "link-hub") {
          if (edge.data.linkMemberId) {
            await deleteTargetEntityRdvLinkMember(
              edge.data.linkMemberId,
              environmentId,
              customerId
            );
          }
          removeRelationshipFromLocalModel(edge);
          removeRelationshipFromSnapshots(edge);
          setEdges((currentEdges) => currentEdges.filter((candidate) => candidate.id !== edge.id));
          setSelectedRelationshipEdgeId(null);
          showStatus(edge.data.linkMemberId ? "Link-to-hub relationship deleted." : "Draft relationship removed.");
          return;
        }

        if (edge.data.relationshipType === "hub-satellite") {
          if (edge.data.satMemberId) {
            await deleteTargetEntityRdvSatMember(
              edge.data.satMemberId,
              environmentId,
              customerId
            );
          }
          removeRelationshipFromLocalModel(edge);
          removeRelationshipFromSnapshots(edge);
          setEdges((currentEdges) => currentEdges.filter((candidate) => candidate.id !== edge.id));
          setSelectedRelationshipEdgeId(null);
          showStatus(edge.data.satMemberId ? "Hub-to-satellite relationship deleted." : "Draft relationship removed.");
          return;
        }

        removeRelationshipFromLocalModel(edge);
        removeRelationshipFromSnapshots(edge);
        setEdges((currentEdges) => currentEdges.filter((candidate) => candidate.id !== edge.id));
        setSelectedRelationshipEdgeId(null);
        showStatus("Draft link-to-satellite association removed.");
      } catch (error) {
        showStatus(getApiErrorMessage(error, "Unable to delete relationship."));
      } finally {
        setDeletingRelationshipEdgeId(null);
      }
    },
    [removeRelationshipFromLocalModel, removeRelationshipFromSnapshots, setEdges, showStatus]
  );

  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof applyEdgeChanges>[0]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );

  const handleNodeClick = useCallback((event: React.MouseEvent, node: FlowNode) => {
    if (event.shiftKey || event.metaKey || event.ctrlKey) {
      return;
    }
    setSelectedRelationshipEdgeId(null);
    if (node.type === "linkNode") {
      setSelectedLinkId(node.id);
      setSelectedNodeId(null);
    } else {
      setSelectedNodeId(node.id);
      setSelectedLinkId(null);
    }
  }, []);

  const handleNodeDoubleClick = useCallback((_: React.MouseEvent, node: FlowNode) => {
    setSelectedRelationshipEdgeId(null);
    if (node.type === "linkNode") {
      setSelectedLinkId(node.id);
      setSelectedNodeId(null);
    } else {
      setSelectedNodeId(node.id);
      setSelectedLinkId(null);
    }
    setDetailsPanelOpen(true);
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedLinkId(null);
    setSelectedRelationshipEdgeId(null);
  }, []);

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent, edge: FlowEdge) => {
      event.stopPropagation();
      setSelectedRelationshipEdgeId(edge.id);
      const linkId =
        edge.data?.relationshipType === "link-hub" ||
        edge.data?.relationshipType === "link-satellite"
          ? edge.data.linkId
          : undefined;
      if (linkId) {
        setSelectedLinkId(linkId);
        setSelectedNodeId(null);
      }
    },
    []
  );

  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: FlowEdge) => {
      event.preventDefault();
      event.stopPropagation();
      setSelectedRelationshipEdgeId(edge.id);
    },
    []
  );

  useEffect(() => {
    if (!selectedRelationshipEdge) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Backspace" && event.key !== "Delete") {
        return;
      }
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      void handleDeleteRelationshipEdge(selectedRelationshipEdge);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteRelationshipEdge, selectedRelationshipEdge]);

  const handleInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstanceRef.current = instance;
    instance.fitView({ padding: FIT_VIEW_PADDING, duration: 800 });
  }, []);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || !model) {
        return;
      }

      const sourceNode = nodes.find((node) => node.id === connection.source);
      const targetNode = nodes.find((node) => node.id === connection.target);
      if (!sourceNode || !targetNode) {
        showStatus("Unable to resolve nodes for connection.");
        return;
      }

      const sourceType = sourceNode.type;
      const targetType = targetNode.type;

      const getByType = (type?: string | null) => {
        if (type === "hubNode") return "hub";
        if (type === "satelliteNode") return "satellite";
        if (type === "linkNode") return "link";
        if (type === "manualNode") return "manual";
        return null;
      };

      const resolvedSource = getByType(sourceType);
      const resolvedTarget = getByType(targetType);

      if (
        (resolvedSource === "hub" && resolvedTarget === "satellite") ||
        (resolvedSource === "satellite" && resolvedTarget === "hub")
      ) {
        const hubId = (sourceType === "hubNode" ? sourceNode.id : targetNode.id) as string;
        const satelliteId = (sourceType === "satelliteNode" ? sourceNode.id : targetNode.id) as string;

        if (!hubId || !satelliteId) {
          showStatus("Unable to map satellite to hub.");
          return;
        }

        updateSatelliteParent(satelliteId, hubId);
        markEntityDirty("satellite", satelliteId);
        showStatus("Satellite assigned to hub.");
        return;
      }

      if (
        (resolvedSource === "link" && resolvedTarget === "hub") ||
        (resolvedSource === "hub" && resolvedTarget === "link")
      ) {
        const linkId = (sourceType === "linkNode" ? sourceNode.id : targetNode.id) as string;
        const hubId = (sourceType === "hubNode" ? sourceNode.id : targetNode.id) as string;

        if (!linkId || !hubId) {
          showStatus("Unable to connect hub to link.");
          return;
        }

        setModel((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            links: previous.links.map((link) => {
              if (link.id !== linkId) {
                return link;
              }
              const alreadyLinked = link.connectedHubIds.includes(hubId);
              const nextConnectedHubIds = alreadyLinked
                ? link.connectedHubIds
                : [...link.connectedHubIds, hubId];
              const nextTargetTable =
                buildLinkTargetTable(nextConnectedHubIds, previous.hubs) ??
                link.record.target_table;
              const hubRecord = previous.hubs.find((candidate) => candidate.id === hubId)?.record;
              const baseRecord =
                hubRecord && link.connectedHubIds.length === 0
                  ? {
                      ...link.record,
                      subject_area: hubRecord.subject_area,
                      ensemble: hubRecord.ensemble
                    }
                  : link.record;

              return {
                ...link,
                connectedHubIds: nextConnectedHubIds,
                record: {
                  ...baseRecord,
                  target_table: nextTargetTable ?? baseRecord.target_table,
                  // Keep the NAME in step with the table. Connecting a hub renames the link
                  // to `<hub>__<hub>_l`, and that is what gets saved — but only target_table
                  // was updated here, so target_entity_rdv_name kept the placeholder minted
                  // at creation (`link_1787664262836_l`). Everything that shows a link reads
                  // the name first, so a link that saved perfectly still displayed as its
                  // temporary id.
                  target_entity_rdv_name:
                    nextTargetTable ?? baseRecord.target_entity_rdv_name
                },
                linkedHubs: (() => {
                  const existing = link.linkedHubs ?? [];
                  if (existing.some((entry) => entry.hub_target_entity_rdv_name === hubId)) {
                    return existing;
                  }
                  return [
                    ...existing,
                    {
                      hub_target_entity_rdv_id: hubRecord?.target_entity_rdv_id ?? null,
                      hub_target_entity_rdv_name: hubRecord?.target_table ?? hubId,
                      hub_role: null
                    }
                  ];
                })()
              };
            })
          };
        });
        setSelectedLinkId(linkId);
        setSelectedNodeId(null);
        markEntityDirty("link", linkId);
        showStatus("Hub connected to link.");
        return;
      }

      if (
        (resolvedSource === "link" && resolvedTarget === "satellite") ||
        (resolvedSource === "satellite" && resolvedTarget === "link")
      ) {
        const linkId = (sourceType === "linkNode" ? sourceNode.id : targetNode.id) as string;
        const satelliteId = (sourceType === "satelliteNode" ? sourceNode.id : targetNode.id) as string;

        if (!linkId || !satelliteId) {
          showStatus("Unable to associate satellite with link.");
          return;
        }

        setModel((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            links: previous.links.map((link) =>
              link.id === linkId
                ? {
                    ...link,
                    satelliteIds: link.satelliteIds.includes(satelliteId)
                      ? link.satelliteIds
                      : [...link.satelliteIds, satelliteId]
                  }
                : link
            )
          };
        });
        setSelectedLinkId(linkId);
        setSelectedNodeId(null);
        markEntityDirty("link", linkId);
        showStatus("Satellite associated with link.");
        return;
      }

      showStatus("Connection not supported for these entities.");
    },
    [markEntityDirty, model, nodes, setModel, showStatus, updateSatelliteParent]
  );

  const currentColumnsList = currentColumns;
  const currentTableMappingsList = currentTableMappings;

  if (!model) {
    return (
      <section className="tw-flex tw-h-[80vh] tw-w-full tw-items-center tw-justify-center tw-rounded-[2.5rem] tw-bg-[#f4f7fd]">
        <p className="tw-text-base tw-font-medium tw-text-slate-600">
          {isMetadataLoading ? "Loading Data Vault workspace…" : "Preparing workspace…"}
        </p>
      </section>
    );
  }

  const hasHubs = model.hubs.length > 0;
  const overlayEntityLabel = deleteOverlay?.name || "";

  return (
    <>
      {deleteOverlay ? (
        <div className="tw-fixed tw-inset-0 tw-z-[2000] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/50 tw-backdrop-blur">
          <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-3xl tw-border tw-border-slate-100 tw-bg-white tw-px-6 tw-py-4 tw-shadow-2xl">
            <span className="tw-inline-flex tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-slate-200 tw-border-t-blue-500" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-slate-900">
                Deleting {overlayEntityLabel || deleteOverlay.type}…
              </p>
              <p className="tw-text-xs tw-text-slate-500">
                Please wait while we update your workspace.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {playModalOpen ? (
        <div className="tw-fixed tw-inset-0 tw-z-[1900] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/40 tw-backdrop-blur-sm">
          <div className="tw-mx-4 tw-flex tw-max-h-[85vh] tw-w-full tw-max-w-6xl tw-flex-col tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-2xl">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div>
                <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
                  Data Vault
                </p>
                <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900">
                  Model Generation
                </h2>
              </div>
              <button
                type="button"
                onClick={handleClosePlayModal}
                className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-sm tw-text-slate-600 hover:tw-bg-slate-100"
              >
                Close
              </button>
            </div>
            <div className="tw-mt-4 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-4">
              <div>
                <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                  Subject area
                </label>
                <select
                  value={playSubjectArea}
                  onChange={(event) => handlePlaySubjectAreaChange(event.target.value)}
                  disabled={isPlaySqlLoading}
                  className="tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
                >
                  <option value="" disabled>
                    Select subject area
                  </option>
                  {availableSubjectAreasForForms.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
                <div>
                  <div className="tw-mt-1 tw-inline-flex tw-rounded-full tw-border tw-border-slate-200 tw-bg-slate-100 tw-p-0.5">
                    {(
                      [
                        { value: "model", label: "Model SQL" },
                        { value: "dq", label: "DQ Tests" }
                      ] as const
                    ).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handlePlayModeChange(option.value)}
                        disabled={isPlaySqlLoading}
                        className={`tw-rounded-full tw-px-4 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed ${
                          playMode === option.value
                            ? "tw-bg-white tw-text-[#03234a] tw-shadow-sm"
                            : "tw-text-slate-500 hover:tw-text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {playMode === "model" && isLakehouseTarget ? (
                  <div>
                    <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                      Inserts
                    </label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={playGenerateInserts}
                      onClick={() => {
                        setPlayGenerateInserts((prev) => !prev);
                        setIsPlaySqlStale(Boolean(playSqlPreview));
                      }}
                      disabled={isPlaySqlLoading}
                      title="Generate INSERT statements in addition to CREATE statements."
                      className={`tw-mt-1 tw-flex tw-h-[34px] tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-text-xs tw-font-semibold tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
                        playGenerateInserts
                          ? "tw-border-[#03234a] tw-bg-[#03234a] tw-text-white"
                          : "tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-border-slate-400"
                      }`}
                    >
                      {playGenerateInserts ? "Inserts: On" : "Inserts: Off"}
                    </button>
                  </div>
                ) : null}
                {playMode === "model" && isLakehouseTarget ? (
                  <div className="tw-min-w-[220px] tw-flex-1">
                    <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                      File system
                    </label>
                    <input
                      type="text"
                      value={playFileSystem}
                      onChange={(event) => {
                        setPlayFileSystem(event.target.value);
                        setIsPlaySqlStale(Boolean(playSqlPreview));
                      }}
                      disabled={isPlaySqlLoading}
                      placeholder="Container name — generated SQL may be faulty if not supplied"
                      className="tw-mt-1 tw-h-[34px] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
                    />
                  </div>
                ) : null}
              </div>
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <p className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                    {playMode === "dq" ? "Generated DQ test(s)" : "Generated SQL"}
                  </p>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {isPlaySqlStale ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-amber-600">
                        Stale
                      </span>
                    ) : null}
                    {playSqlCopyStatus === "success" ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-emerald-600">
                        Copied
                      </span>
                    ) : playSqlCopyStatus === "error" ? (
                      <span className="tw-text-[11px] tw-font-medium tw-text-rose-600">
                        Copy failed
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void handleGeneratePlaySqlPreview()}
                      disabled={!playSubjectArea || isPlaySqlLoading}
                      className="tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white hover:tw-bg-[#021b39] focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                    >
                      {isPlaySqlLoading ? "Generating..." : "Generate"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyPlaySqlPreview}
                      disabled={!playSqlPreview || isPlaySqlLoading}
                      className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[#03234a] hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                {playSqlError ? (
                  <p className="tw-mt-3 tw-rounded-2xl tw-bg-rose-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-rose-700">
                    {playSqlError}
                  </p>
                ) : null}
                {playSqlWarning ? (
                  <p className="tw-mt-3 tw-rounded-2xl tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-amber-700">
                    {playSqlWarning}
                  </p>
                ) : null}
                <GenerationReportSummary report={playGenerationReport} />
                <div className="tw-mt-2 tw-min-h-0 tw-w-full tw-min-w-0 tw-max-w-full tw-flex-1 tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                  {isPlaySqlLoading ? (
                    <p className="tw-text-sm tw-text-slate-500">
                      Generating SQL preview...
                    </p>
                  ) : playSqlPreview ? (
                    <SqlPreview value={playSqlPreview} wrap showLineNumbers />
                  ) : (
                    <p className="tw-text-sm tw-text-slate-500">
                      Select a subject area, then generate SQL preview.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <PromoteRdvAssetModal
        open={promoteModalOpen}
        onClose={() => setPromoteModalOpen(false)}
        subjectAreaOptions={availableSubjectAreasForForms}
        hubOptions={(model?.hubs ?? [])
          .filter((hub) => hub.record.target_entity_rdv_id)
          .map((hub) => ({
            id: String(hub.record.target_entity_rdv_id),
            name: hub.record.target_entity_rdv_name || String(hub.record.target_entity_rdv_id)
          }))}
        onPromoted={() => {
          showStatus("Entity promoted to the Raw Data Vault.");
          void loadWorkspaceModel();
        }}
      />
      <section className="tw-flex tw-h-[90vh] tw-w-full tw-flex-col tw-gap-4 tw-rounded-[2.5rem] tw-bg-[#f4f7fd] ">
      {metadataError ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-amber-200 tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-amber-600 tw-shadow">
          <span>Unable to load existing Data Vault metadata.</span>
          <button
            type="button"
            onClick={handleRefreshWorkspace}
            disabled={isMetadataLoading}
            className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-amber-700 hover:tw-bg-amber-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
          >
            {isMetadataLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      ) : null}
      {/* <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-4"> */}
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ToolbarButton icon={FiPlus} label="Add Hub" onClick={handleAddHub} />
            <ToolbarButton
              icon={FiPlus}
              label="Add Satellite"
              onClick={handleAddSatellite}
              disabled={!hasHubs}
            />
            <ToolbarButton icon={FiPlus} label="Add Link" onClick={handleAddLink} />
            <ToolbarButton
              icon={FiPlus}
              label="Promote from Catalog"
              onClick={() => setPromoteModalOpen(true)}
            />
            <ToolbarButton icon={FiDownload} label="Export Model" onClick={handleSaveModel} />
            <ToolbarButton
              icon={FiGrid}
              label="Auto Arrange"
              onClick={handleAutoArrangeLayout}
              disabled={!nodes.length}
            />
            <ToolbarButton
              icon={FiAlignJustify}
              label="Save Layout"
              onClick={handleSaveLayout}
              disabled={!hasUnsavedLayoutChanges}
            />
            {/* <ToolbarButton icon={FiShield} label="Validate" onClick={handleValidate} /> */}
          </div>
          <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                onClick={() => setFilterPanelOpen(true)}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
                </svg>
                Filter
              </button>
              <input
                type="text"
                value={nodeSearchQuery}
                onChange={(event) => setNodeSearchQuery(event.target.value)}
                placeholder="Search node by target_entity_rdv_name…"
                className="tw-w-56 tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
              />
              {nodeSearchQuery ? (
                <button
                  type="button"
                  onClick={() => setNodeSearchQuery("")}
                  className="tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-border-slate-400"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div>
              <ToolbarButton icon={FiPlay} label="Run" onClick={handleOpenPlayModal} />
            </div>
          </div>
        </div>
      {/* </div> */}
      {validationVisible ? <ValidationSummary messages={validationMessages} /> : null}
      <main className="tw-flex tw-w-full tw-flex-1 tw-min-h-0">
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-min-h-[24rem] tw-flex-1 tw-rounded-[2.25rem] tw-border tw-border-slate-100 tw-bg-white tw-p-4 tw-shadow-[0_30px_80px_-50px_rgba(15,23,42,0.55)]">
          <div className="tw-absolute tw-right-6 tw-top-6 tw-z-10 tw-flex tw-flex-col tw-items-end tw-gap-2">
            {selectedEntity ? (
              <button
                type="button"
                onClick={() => setDetailsPanelOpen(true)}
                className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
              >
                View details
              </button>
            ) : null}
            {selectedRelationshipEdge ? (
              <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-rose-200 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow">
                <span>
                  {selectedRelationshipEdge.data?.relationshipType === "link-hub"
                    ? "Link -> Hub"
                    : selectedRelationshipEdge.data?.relationshipType === "hub-satellite"
                    ? "Hub -> Satellite"
                    : "Link -> Satellite"}
                </span>
                <button
                  type="button"
                  onClick={() => void handleDeleteRelationshipEdge(selectedRelationshipEdge)}
                  disabled={deletingRelationshipEdgeId === selectedRelationshipEdge.id}
                  className="tw-rounded-full tw-bg-rose-600 tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white hover:tw-bg-rose-700 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
                >
                  {deletingRelationshipEdgeId === selectedRelationshipEdge.id ? "Deleting" : "Delete"}
                </button>
              </div>
            ) : null}
            {statusMessage ? (
              <div className="tw-rounded-full tw-border tw-border-blue-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-blue-700 tw-shadow">
                {statusMessage}
              </div>
            ) : null}
          </div>
          <ReactFlow
            nodes={highlightedFlow.nodes}
            edges={highlightedFlow.edges}
            nodeTypes={nodeTypes}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            onNodeDragStop={handleNodeDragStop}
            onPaneClick={handlePaneClick}
            onEdgeClick={handleEdgeClick}
            onEdgeContextMenu={handleEdgeContextMenu}
            onInit={handleInit}
            {...SHARED_CANVAS_INTERACTION_PROPS}
            fitView
            fitViewOptions={{ padding: FIT_VIEW_PADDING }}
            onConnect={handleConnect}
            connectionMode={ConnectionMode.Loose}
            className="tw-h-full tw-w-full tw-rounded-[1.75rem] tw-bg-[#f6f7fb]"
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: false,
              style: {
                strokeWidth: 1.5,
                stroke: "#cbd5f5"
              }
            }}
          >
            <Background gap={[24, 24]} size={1} color="#e2e8f0" style={{ opacity: 0.6 }} />
            <MiniMap
              className="!tw-rounded-2xl !tw-border !tw-border-slate-200 !tw-bg-white !tw-shadow-lg"
              nodeStrokeColor={(node: FlowNode) =>
                node.type === "hubNode"
                  ? "#2563eb"
                  : node.type === "linkNode"
                  ? "#ef4444"
                  : "#22c55e"
              }
              nodeColor={(node: FlowNode) =>
                node.type === "hubNode"
                  ? "#dbeafe"
                  : node.type === "linkNode"
                  ? "#fee2e2"
                  : "#dcfce7"
              }
              maskColor="rgba(15, 23, 42, 0.1)"
            />
            <Controls
              className="!tw-rounded-full !tw-border !tw-border-slate-200 !tw-bg-white !tw-shadow-lg"
              position="bottom-left"
              showInteractive={false}
            />
          </ReactFlow>
        </div>
      </main>
      <div
        className={`tw-fixed tw-inset-0 tw-z-[1001] tw-transition-opacity tw-duration-300 tw-ease-in-out ${
          isFilterPanelOpen ? "tw-pointer-events-auto tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
        }`}
        aria-hidden={!isFilterPanelOpen}
      >
        <div
          className="tw-absolute tw-inset-0 tw-bg-slate-900/15 tw-backdrop-blur-sm"
          onClick={() => setFilterPanelOpen(false)}
        />
        <div
          className={`tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-full tw-max-w-full sm:tw-w-[24rem] tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isFilterPanelOpen ? "tw-translate-x-0" : "tw-translate-x-full"
          }`}
        >
          <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-border-l tw-border-slate-200 tw-bg-white tw-shadow-[0_32px_80px_-40px_rgba(15,23,42,0.75)]">
            <button
              type="button"
              onClick={() => setFilterPanelOpen(false)}
              className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600"
              aria-label="Close filter panel"
            >
              ×
            </button>

            <div className="tw-flex tw-h-full tw-flex-col tw-gap-5 tw-p-6 tw-pt-16">
              <div>
                <p className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-blue-600">
                  Canvas Filter
                </p>
                <h3 className="tw-mt-2 tw-text-lg tw-font-semibold tw-text-slate-900">
                  Filter Data Vault Nodes
                </h3>
              </div>

              <label className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                  Filter Field
                </span>
                <select
                  value={filterField}
                  onChange={(event) => {
                    const next = event.target.value as VaultFilterField;
                    setFilterField(next);
                    if (!next) {
                      setFilterValueDraft("");
                    }
                  }}
                  className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                >
                  <option value="">Select filter field</option>
                  <option value="subject_area">subject_area</option>
                  <option value="ensemble">ensemble</option>
                  <option value="target_entity_rdv_name">target_entity_rdv_name</option>
                </select>
              </label>

              {filterField ? (
                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-slate-600">
                    Filter Value
                  </span>
                  {filterField === "subject_area" || filterField === "ensemble" ? (
                    <SearchableSelect
                      value={filterValueDraft}
                      options={filterValueOptions}
                      onChange={setFilterValueDraft}
                      placeholder={`Select ${filterField}…`}
                      aria-label="Filter value"
                      inputClassName="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    />
                  ) : (
                    <input
                      type="text"
                      value={filterValueDraft}
                      onChange={(event) => setFilterValueDraft(event.target.value)}
                      placeholder={`Enter ${filterField} value`}
                      className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                    />
                  )}
                </label>
              ) : null}

              <div className="tw-mt-auto tw-flex tw-gap-2">
                <button
                  type="button"
                  onClick={handleFilterClear}
                  className="tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleFilterApply}
                  className="tw-flex-1 tw-rounded-lg tw-bg-[#03234a] tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#0a3f7f]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {model && selectedEntity ? (
        <>
          <div
            className={`tw-fixed tw-inset-x-0 tw-top-16 tw-bottom-0 tw-z-[1000] tw-bg-slate-900/30 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300 ${
              detailsPanelOpen ? "tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
            }`}
            onClick={handleCloseDetailsPanel}
          />
          <div
            className={`tw-fixed tw-right-4 tw-top-[4.75rem] tw-bottom-4 tw-z-[1100] tw-flex tw-w-[min(34rem,calc(100vw-2rem))] tw-transform tw-transition-transform tw-duration-300 ${
              detailsPanelOpen ? "tw-translate-x-0" : "tw-translate-x-full"
            }`}
          >
            <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-slate-200 tw-bg-white tw-shadow-[0_28px_60px_rgba(15,23,42,0.24)]">
              <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-flex-col tw-gap-3 tw-border-b tw-border-slate-200 tw-bg-white/95 tw-p-5 tw-backdrop-blur-sm">
                <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {selectedEntityChip ? (
                      <span className={selectedEntityChip.className}>{selectedEntityChip.label}</span>
                    ) : null}
                    <span
                      className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-[0.22em] ${
                        selectedEntityIsDirty
                          ? "tw-bg-amber-50 tw-text-amber-700"
                          : "tw-bg-emerald-50 tw-text-emerald-700"
                      }`}
                    >
                      {selectedEntityIsDirty ? "Unsaved changes" : "Up to date"}
                    </span>
                  </div>
                  <h2 className="tw-max-w-full tw-break-words tw-text-[1.7rem] tw-font-semibold tw-leading-tight tw-text-slate-900">
                    {formatLabel(selectedEntity.entity.record.target_table) ||
                      selectedEntity.entity.record.target_table ||
                      "Details"}
                  </h2>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                  <button
                    type="button"
                    onClick={handleCloseDetailsPanel}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveSelectedEntity}
                    disabled={!canSaveSelectedEntity || isSelectedEntitySaving}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                  >
                    {isSelectedEntitySaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              <div className="tw-flex-1 tw-overflow-y-auto tw-p-5 tw-pr-3">
                <EntityDetailsPanel
                  entity={selectedEntity}
                  model={model}
                  onOpenMappings={() => setMappingModalOpen(true)}
                  onHubFieldChange={handleHubFieldChange}
                  onManualFieldChange={handleManualFieldChange}
                  onSatelliteFieldChange={handleSatelliteFieldChange}
                  onSatelliteParentChange={handleSatelliteParentChange}
                  onLinkHubsChange={handleLinkHubsChange}
                  onLinkFieldChange={handleLinkFieldChange}
                  onRemoveHub={handleRemoveHub}
                  onRemoveSatellite={handleRemoveSatellite}
                  onRemoveLink={handleRemoveLink}
                  onRemoveManual={handleRemoveManual}
                  onSave={handleSaveSelectedEntity}
                  onCancel={handleCancelChanges}
                  canSave={canSaveSelectedEntity}
                  isSaving={isSelectedEntitySaving}
                  isDirty={selectedEntityIsDirty}
                  errorMessage={saveError}
                  successMessage={saveSuccess}
                  subjectAreaOptions={availableSubjectAreasForForms}
                  onAddSubjectArea={registerSubjectArea}
                  dwTargetType={activeDwTargetType}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}
      <MappingModal
        open={Boolean(mappingModalOpen && selectedEntity)}
        onClose={() => setMappingModalOpen(false)}
        columns={currentColumnsList}
        tableMappings={currentTableMappingsList}
        selectedEntity={selectedEntity}
      />
      </section>
    </>
  );
}

export function MappingWorkspace(): JSX.Element {
  return (
    <ReactFlowProvider>
      <MappingWorkspaceInner />
    </ReactFlowProvider>
  );
}
